import {
  CARD_BALANCE,
  CARD_LEVIATHAN_ENTRY_BPS,
  cardBeamWidthFor,
  cardSphereRadiusFor,
  cardSuperCannonBodies,
  hullScaleFactor,
} from '@kenocyber/game-domain';
import {
  OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK,
  openWorldEnvelopeZoomFor,
  openWorldZoomFor,
} from '@kenocyber/game-domain/open-world/client';
import {
  PVP_FIREBALL_REACH_BPS,
  PVP_SOLAR_LANCE_REACH_BPS,
  PVP_SOLAR_LANCE_WIDTH_BPS,
  PVP_TICK_MS,
  pvpHullScaleBps,
} from '@kenocyber/game-domain/pvp';
import type { AssetManifest, PvpAppearance } from '@kenocyber/shared';
import Phaser from 'phaser';
import type { OpenWorldCreatureCards, OpenWorldEntityCards } from '../open-world/connection';
import { OpenWorldIntentSource } from '../open-world/controls';
import {
  easeOpenWorldPlayerZoom,
  OpenWorldOwnHullPredictor,
  type OpenWorldPoint,
  OpenWorldPresentationCursor,
  OpenWorldTrack,
  openWorldDrawsAllyRing,
  openWorldLinearShotAt,
  openWorldPlayerZoomAfterWheel,
  pruneOpenWorldTracks,
  releaseOpenWorldIntentOnFocusLoss,
} from '../open-world/presentation';
import { pvpHullRotation } from '../pvp/hull-heading';
import {
  PROJECTILE_TRAIL_ALPHA,
  PROJECTILE_TRAIL_EVERY_FRAMES,
  PROJECTILE_TRAIL_LIFESPAN_MS,
  PROJECTILE_TRAIL_MAX,
  pvpProjectileColour,
  pvpStreakScale,
} from '../pvp/projectile-art';
import { PVP_MU_PER_UNIT, pvpWorldUnits } from '../pvp/scene-model';
import {
  PVP_CANNON_WORLD_UNITS,
  PVP_HULL_WORLD_UNITS,
  pvpCannonArt,
  pvpCannonMounts,
  pvpCannonPivotAngle,
  pvpCannonSpriteRotation,
  pvpMountInWorld,
  pvpShipArt,
  pvpSpriteScale,
  pvpVisualScale,
} from '../pvp/ship-art';
import { assetUrl, findStaticAsset } from './assets';
import { AtomicBlastSystem } from './atomic-blast';
import { BastionFx } from './bastion-fx';
import type { GameBridge } from './bridge';
import { CARD_FIREBALL_BODY_FRACTION, type CardBlastBody, CardBlastFx } from './card-blast-fx';
import { cardEffectTint } from './card-colour';
import { MagicCircleFx, SolarLanceFx } from './card-fx';
import {
  bladeOrbitRadius,
  cardVisualFlags,
  chargingSphereGeometry,
  LEVIATHAN_MIN_HULLS,
  summonDiameter,
} from './card-vfx';
import { CLOAK_HULL_ALPHA, MovementFx } from './movement-fx';
import { type BossDrawEvent, type BossDrawState, OpenWorldBossArt } from './open-world-boss-art';
import { openWorldBossEventsForArt } from './open-world-boss-events';
import {
  type BossCoreDraw,
  type BossEmberDraw,
  type BossHomingDraw,
  type BossShellDraw,
  type BossStrikeDraw,
  type BossSwarmDraw,
  type BossUltimateDraw,
  OpenWorldBossStrikeFx,
} from './open-world-boss-strike-fx';
import { type BoxDrawState, ensureAllyRingTexture, OpenWorldBoxArt } from './open-world-box-art';
import {
  type CometDrawEvent,
  type CometDrawState,
  OpenWorldCometArt,
} from './open-world-comet-art';
import { type OpenWorldMapBodyKind, requestOpenWorldMapBody } from './open-world-map-art';
import { type MerchantDrawState, OpenWorldMerchantArt } from './open-world-merchant-art';
import { type MobDrawEvent, type MobDrawState, OpenWorldMobArt } from './open-world-mob-art';
import {
  OpenWorldPlanetArt,
  type PlanetDrawEvent,
  type PlanetDrawState,
} from './open-world-planet-art';
import {
  openWorldShotDrawsAsBolt,
  openWorldShotGauge,
  openWorldShotHeading,
  openWorldShotSpriteScale,
  openWorldTrailThickness,
} from './open-world-shot-art';
import { OpenWorldStarfield } from './open-world-starfield';
import {
  type FisherDrawState,
  OpenWorldWaterArt,
  type OwnFishingDrawState,
  type WaterDrawState,
} from './open-world-water-art';
import {
  DASH_ORIGIN_X,
  DASH_TEXTURE_HEIGHT,
  DASH_TEXTURE_WIDTH,
  ensureDashTexture,
  ensureShockwaveTexture,
  ensureStreakTexture,
} from './projectile-art';
import { requestSharedTexture } from './shared-texture-requests';
import { MalwareCastFx, MarkCastFx, StatusFx } from './status-fx';
import {
  buildSummonBody,
  requestDroneTexture,
  requestLeviathanTexture,
  type SummonBuiltAs,
  setSummonBoardingArrows,
  setSummonHealth,
  spinSummonRotor,
  summonBuiltAs,
} from './summon-art';

/**
 * **A cena de um quadrante do mundo aberto.**
 *
 * ## Porque é uma cena própria
 *
 * Porque `PvpScene` é a cena de um DUELO, e um duelo é uma arena fechada com
 * duas cadeiras, uma contagem decrescente e um resultado. Um quadrante é um
 * sítio: a câmara anda, o fundo é infinito, a população entra e sai por uma área
 * de interesse, e a nave muda de sala sem a página recarregar.
 *
 * Fazer da `PvpScene` uma classe universal para servir as duas seria o caminho
 * mais curto para uma classe que ninguém consegue mudar sem partir a outra. O
 * que se reutiliza é o que é genuinamente comum e já vive fora dela: a arte do
 * casco, a escala aprovada, a geometria dos canhões, a orientação e as texturas
 * das estrelas.
 *
 * **Nenhuma fórmula de movimento ou de escala é copiada.** `pvpVisualScale`,
 * `pvpSpriteScale`, `pvpMountInWorld` e `pvpHullRotation` são chamados, e é por
 * isso que uma nave mede aqui exatamente o que mede num duelo.
 *
 * ## O que ela desenha, e de onde
 *
 * Só o que o servidor disse. As entidades vêm da área de interesse, os tiros vêm
 * dos frames, e nada aparece porque o cliente achou que devia — não há
 * simulação, não há predição de dano e não há um único objeto inventado.
 */

/** Uma unidade de arena por cada mil milli-unidades. A conversão do duelo, lida. */
const MU = PVP_MU_PER_UNIT;

/** Chave da textura de recurso, quando a arte de uma nave ainda não chegou. */
const PLACEHOLDER = 'open-world-hull-placeholder';

export interface OpenWorldSceneEntity {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  /** A intenção de movimento que a autoridade aplicou (gate V28I.BX.2). */
  readonly moveX?: number;
  readonly moveY?: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
  readonly radiusMu: number;
  readonly appearance: PvpAppearance | null;
  readonly connected: boolean;
  /** Esta nave voa comigo, segundo a autoridade. Nunca uma cor do navegador. */
  readonly ally: boolean;
  /** O que as cartas estão a fazer a este casco, e a desenhar nele. */
  readonly cards: OpenWorldEntityCards;
}

export interface OpenWorldSceneShot {
  /**
   * **O que a sala disse deste corpo**, e a cena só lê (gate V28I.AK).
   *
   * Separado da posição desenhada de propósito: um corpo linear move-se a cada
   * trama sem que nada disto mude, e misturar as duas coisas obrigava a
   * reconstruir o objeto inteiro só para mover dois números.
   */
  readonly base: {
    readonly id: number;
    /** Que corpo de carta ele é, ou nada. A cena não o deduz. */
    readonly card: 'f' | 'c' | 'z' | 't' | 's' | 'w' | 'g' | 'd' | null;
    /** O raio da explosão que ele vai fazer. Só com `card`, e só se rebenta. */
    readonly blastMu: number | null;
    /** Quem o disparou. É por aqui que a cena encontra a cor do canhão. */
    readonly owner: number;
    /** Tiro, bomba ou míssil, como a sala o disse. */
    readonly kind: 'shot' | 'bomb' | 'missile';
    /** A escala física congelada no disparo, em basis points (gate V28I.CF.3). */
    readonly scaleBps: number;
    /** Onde um obus cai: o centro do círculo dele. Só um obus o tem (gate V28I.CF.1A). */
    readonly targetX?: number;
    readonly targetY?: number;
    /**
     * **A âncora de um corpo linear**, tal como a sala a disse (gate V28I.BX.1).
     * Com ela, o corpo desenha-se em `base + v x (tique - tique_da_base)` no
     * tique do cursor de apresentação; sem ela, em `x` e `y`.
     */
    readonly linear?: boolean;
    readonly baseX?: number;
    readonly baseY?: number;
    readonly baseTick?: number;
    readonly vx?: number;
    readonly vy?: number;
  };
  /**
   * A posição DESENHADA, derivada pela camada de apresentação da ligação.
   *
   * A cena lê-a e põe lá o sprite. Ela não a calcula, não a corrige e não decide
   * nada com ela: a aritmética é a do V28I.Z.3, e vive num sítio só.
   */
  readonly x: number;
  readonly y: number;
}

/** Uma explosão de carta, desenhada uma vez e esquecida. */
export interface OpenWorldSceneCardBurst {
  readonly id: number;
  readonly kind: 'f' | 'c';
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
}

/** Uma explosão de área confirmada. Hoje, o kamikaze de um Drone. */
export interface OpenWorldSceneBlast {
  /** A semente da variação, composta do tique e do autor. Nunca um sorteio. */
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
}

/** Uma invocação, como a cena a recebe. O Drone e o Leviatã. */
export interface OpenWorldSceneSummon {
  readonly id: number;
  readonly kind: 'd' | 'l';
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  readonly ownerId: number;
  readonly healthBps: number;
  readonly pilotId: number | null;
  readonly radiusMu: number;
}

/** O que a cena precisa de saber, e é substituído por inteiro a cada frame. */
export interface OpenWorldSceneFrame {
  readonly me: number;
  readonly sideMu: number;
  readonly entities: readonly OpenWorldSceneEntity[];
  readonly shots: readonly OpenWorldSceneShot[];
  /** Está o PRÓPRIO casco protegido pela fronteira. Um facto, nunca um relógio. */
  readonly protectedNow: boolean;
  /** As rochas na vista, tal como a autoridade as descreveu. */
  readonly comets: readonly CometDrawState[];
  /** O que lhes aconteceu nesta trama. Efeitos, desenhados uma vez. */
  readonly cometEvents: readonly CometDrawEvent[];
  /** Os planetas na vista, tal como a autoridade os descreveu. */
  readonly planets: readonly PlanetDrawState[];
  readonly planetEvents: readonly PlanetDrawEvent[];
  /** Os mobs na vista, tal como a autoridade os descreveu. */
  readonly mobs: readonly MobDrawState[];
  readonly mobEvents: readonly MobDrawEvent[];
  /** Os bosses na vista, tal como a autoridade os descreveu. */
  readonly bosses: readonly BossDrawState[];
  /**
   * O que lhes aconteceu. `radiusMu` e o raio MECANICO da area, quando o evento
   * teve uma — a cena converte-o para unidades de desenho antes de o entregar
   * a arte, pelas mesmas unidades de tudo o resto.
   */
  readonly bossEvents: readonly (BossDrawEvent & { readonly radiusMu?: number })[];
  /**
   * **Os golpes físicos em curso**, um por boss corpo a corpo que esteja a
   * atacar. A geometria é a autoritativa, e é ela que se desenha.
   */
  readonly bossStrikes: readonly { readonly id: string; readonly strike: BossStrikeDraw | null }[];
  /** As brasas no chão, com os dois relógios da sala. */
  readonly bossEmbers: readonly BossEmberDraw[];
  /**
   * **Os teleguiados no ar e os núcleos rúnicos no campo** (gate V28I.BG).
   *
   * Corpos autoritativos, um desenho por identidade. A cena não decide acerto
   * nenhum e não conta tempo nenhum: ela desenha o que a sala diz que existe.
   */
  readonly bossHoming: readonly BossHomingDraw[];
  readonly bossCores: readonly BossCoreDraw[];
  /**
   * **O tique autoritativo desta trama.**
   *
   * As brasas medem o que lhes resta contra ELE, e nunca contra o relógio do
   * navegador: o cliente apresenta o tempo da sala, e não conta um seu.
   */
  readonly tick: number;
  /** Quando a trama chegou ao socket, no relógio de apresentação (gate V28I.BX.2). */
  readonly receivedAt?: number;
  /** O instante local a que este tique corresponde para o próprio casco, ou nada. */
  readonly ownSyncAt?: number | null;
  /** As áreas aquáticas na vista. Lugares, e nada sobre o que vive nelas. */
  readonly waters: readonly WaterDrawState[];
  readonly fishers: readonly FisherDrawState[];
  readonly fishing: OwnFishingDrawState | null;
  /** O comerciante deste porto, ou nada. Um marco publico. */
  readonly merchant: MerchantDrawState | null;
  /** As caixas de morte na vista. Marcas publicas, sem conteudo. */
  readonly boxes: readonly BoxDrawState[];
  /** A que o servidor disse que eu posso abrir. Ganha um realce. */
  readonly nearestBox: string | null;
  /** As invocações na vista. Entram inteiras e saem por id. */
  readonly summons: readonly OpenWorldSceneSummon[];
  /** As explosões de carta deste quadro. Substituídas por inteiro. */
  readonly cardBursts: readonly OpenWorldSceneCardBurst[];
  /** E as de área, do canal dos efeitos de joia. */
  readonly blasts: readonly OpenWorldSceneBlast[];
}

export interface OpenWorldSceneOptions {
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
}

/** Um quadrante vazio. O que a cena desenha antes de o primeiro frame chegar. */
const NO_FRAME: OpenWorldSceneFrame = {
  me: 0,
  sideMu: 0,
  entities: [],
  shots: [],
  protectedNow: false,
  comets: [],
  cometEvents: [],
  planets: [],
  planetEvents: [],
  mobs: [],
  mobEvents: [],
  bosses: [],
  bossEvents: [],
  bossStrikes: [],
  bossEmbers: [],
  bossHoming: [],
  bossCores: [],
  tick: 0,
  waters: [],
  fishers: [],
  fishing: null,
  merchant: null,
  boxes: [],
  nearestBox: null,
  summons: [],
  cardBursts: [],
  blasts: [],
};

/**
 * A que profundidade um casco é desenhado.
 *
 * Nomeada porque deixou de ser lida num sítio só: o brilho da proteção assenta
 * em cima dela, e um dos dois números escrito à mão acabaria por passar por
 * baixo do outro sem ninguém saber porquê.
 */
const HULL_DEPTH = 10;

/**
 * Quanto a barra de vida se levanta acima da borda do casco.
 *
 * O mesmo dez que `drawBar` usa, mais a espessura dela: é a partir daqui que
 * um selo ou uma estrela se empilham, e um número solto em dois sítios seria
 * um selo a cair em cima da barra no dia em que ela mudasse.
 */
const BAR_LIFT_UNITS = 14;

/** E quanto a etiqueta do Manto se levanta acima disso. O mesmo do duelo. */
const CLOAK_TAG_LIFT = 15;

/**
 * **A letra de um corpo de carta, e o nome que a família de arte usa.**
 *
 * Uma tabela fechada e não um `switch`: uma letra nova não compila sem um nome,
 * e a cena nunca tem de adivinhar o que desenhar.
 */
const CARD_BODY_KIND: Readonly<
  Record<
    'f' | 'c' | 'z' | 't' | 's',
    'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt'
  >
> = Object.freeze({
  f: 'fireball',
  c: 'catole',
  z: 'ice_sphere',
  t: 'ice_star',
  s: 'shock_bolt',
});

interface HullView {
  readonly container: Phaser.GameObjects.Container;
  readonly hull: Phaser.GameObjects.Image;
  readonly cannons: Phaser.GameObjects.Image[];
  readonly bar: Phaser.GameObjects.Graphics;
  /** Onde a nave está a ser DESENHADA. Interpolado, e nunca a posição crua. */
  drawX: number;
  drawY: number;
  builtFrom: string;
}

export class OpenWorldScene extends Phaser.Scene {
  static readonly KEY = 'open-world';

  private starfield: OpenWorldStarfield | null = null;
  /**
   * **O brilho da proteção de fronteira**, e é a composição do Bastião.
   *
   * A MESMA classe, e não uma cópia: estrelas de quatro pontas a orbitar, o
   * halo branco/dourado/néon a pulsar devagar, e o casco a cintilar por baixo.
   * Escrever uma segunda versão aqui seria uma segunda versão a divergir da
   * primeira, que é exatamente a lição que `bastion-fx.ts` já herdou uma vez.
   *
   * **Só a apresentação.** Nada aqui importa a carta, consulta o seu nível,
   * consome a sua espera ou lhe toca no temporizador: a sala do mundo aberto
   * não tem Bastião nenhum. O que decide se isto se vê é o `bs` autoritativo,
   * e esta classe não tem relógio próprio — recebe `null` e apaga no mesmo
   * quadro em que a proteção acaba, seja por tempo, travessia, extração, morte,
   * desconexão, correção ou `SHUTDOWN`.
   */
  private protection: BastionFx | null = null;
  /**
   * As rochas desenhadas.
   *
   * Construída ao abrir e largada ao fechar, como o campo de estrelas: os
   * objetos de uma expedição não sobrevivem à seguinte, e a identidade
   * autoritativa de cada pedra é a chave — nada é herdado por posição.
   */
  private cometArt: OpenWorldCometArt | null = null;
  /** Os planetas desenhados. Mesma disciplina, silhueta propria. */
  private planetArt: OpenWorldPlanetArt | null = null;
  /** Os mobs desenhados. Corpo, estado e efeitos, nessa ordem. */
  private mobArt: OpenWorldMobArt | null = null;
  /** Os bosses desenhados, com os sistemas aprovados das duas magias. */
  private bossArt: OpenWorldBossArt | null = null;
  /** Os avisos e as execuções dos seis golpes corpo a corpo (gate V28I.BA). */
  private bossStrikeFx: OpenWorldBossStrikeFx | null = null;
  /** Reutilizado a cada trama. Nunca uma alocação nova dentro de um quadro. */
  private readonly ultimateBodies: BossUltimateDraw[] = [];
  /** As esferas de enxame deste quadro. Um buffer do campo, nunca do quadro. */
  private readonly swarmBodies: BossSwarmDraw[] = [];
  /** As balas rotativas deste quadro. Um buffer do campo, nunca do quadro. */
  private readonly rotaryBodies: BossSwarmDraw[] = [];
  /** Os obuses do Bombardeio de Profundidade, na posição desenhada (gate V28I.CF.1A). */
  private readonly shellBodies: BossShellDraw[] = [];
  /** A água desenhada, e a linha de quem está a pescar. */
  private waterArt: OpenWorldWaterArt | null = null;
  /** A estacao comercial de um porto. Abaixo dos cascos: e um marco. */
  private merchantArt: OpenWorldMerchantArt | null = null;
  /**
   * **O aro que diz quem voa comigo** (gate V28I.Q).
   *
   * Um anel fino por baixo da nave, e nada mais: sem partículas, sem halo por
   * cima do casco, sem pulsação. Ele não pode tapar a vida, o nickname, as
   * cartas nem os sinais de debuff — quem voa com quem é útil, e nada disso é
   * mais urgente do que saber quem está a morrer.
   *
   * Uma textura, criada uma vez e partilhada por todos: um anel por aliado
   * seria uma textura por aliado, e nada aqui é criado por quadro.
   */
  private readonly allyRings = new Map<number, Phaser.GameObjects.Image>();
  /** As caixas desenhadas. Abaixo das naves e dos tiros, acima do fundo. */
  private boxArt: OpenWorldBoxArt | null = null;
  /**
   * **As cartas, desenhadas pelas MESMAS famílias que o duelo usa.**
   *
   * `CardBlastFx` para os corpos e as explosões, `MagicCircleFx` para o círculo
   * do Turbilhão, `SolarLanceFx` para o feixe, e `summon-art.ts` para o Drone e
   * o Leviatã. Nenhuma linha de arte foi copiada para aqui: o que este ficheiro
   * faz é dar-lhes o estado autoritativo deste mundo.
   */
  private cardBlasts: CardBlastFx | null = null;
  private readonly cardCircles = new Map<number, MagicCircleFx>();
  private readonly cardLances = new Map<number, SolarLanceFx>();
  private readonly summons = new Map<number, Phaser.GameObjects.Container>();
  private readonly summonBuilt = new Map<number, SummonBuiltAs>();
  /** Se há uma estrutura vazia ao alcance. Publicado só quando MUDA. */
  private boardOffer = false;
  /**
   * **Os sinais de debuff, o movimento e os dois pulsos de lançamento.**
   *
   * Um por entidade e por família, criado à primeira vez que faz falta e
   * destruído quando a entidade sai da vista. As classes são as MESMAS do
   * duelo — nenhuma linha de arte foi escrita aqui — e o que este ficheiro faz
   * é dar-lhes o estado autoritativo deste mundo.
   */
  private readonly cardStatus = new Map<number, StatusFx>();
  /**
   * **Os mesmos sinais, sobre uma CRIATURA.**
   *
   * A mesma família e a mesma arte: um alienígena congelado leva a casca de
   * gelo de um casco congelado, porque é o mesmo estado. Chaveados pela
   * identidade pública da vaga, que é uma string — daí um mapa próprio, e não
   * o dos cascos.
   */
  private readonly creatureStatus = new Map<string, StatusFx>();
  private readonly cardMovement = new Map<number, MovementFx>();
  private readonly cardMalware = new Map<number, MalwareCastFx>();
  private readonly cardMarks = new Map<number, MarkCastFx>();
  private readonly cardBastions = new Map<number, BastionFx>();
  /** O anel de gelo de um casco congelado. Um objeto de forma, reutilizado. */
  private readonly cardAuras = new Map<number, Phaser.GameObjects.Arc>();
  /** A etiqueta INVISÍVEL de quem está encoberto, para o próprio e os aliados. */
  private readonly cloakTags = new Map<number, Phaser.GameObjects.Text>();
  /** Os dois canhões temporários montados, por casco. */
  private readonly superCannons = new Map<number, Phaser.GameObjects.Image[]>();
  /** As explosões de área, com a arte aprovada do Núcleo Atómico. */
  private atomicBlasts: AtomicBlastSystem | null = null;
  /**
   * As esferas a carregar deste quadro, recolhidas e entregues de uma vez.
   *
   * Uma lista reutilizada e esvaziada por quadro em vez de uma nova: ela é
   * escrita e lida no mesmo passo, e alocar um array por quadro seria uma
   * alocação por quadro para transportar no máximo um punhado de esferas.
   */
  private readonly chargingSpheres: { key: string; x: number; y: number; radius: number }[] = [];
  private readonly hulls = new Map<number, HullView>();
  private readonly shots = new Map<number, Phaser.GameObjects.Image>();
  /**
   * **Corpos disponíveis para reutilização** (gate V28I.AJ).
   *
   * Um tiro criava um `Image` ao nascer e destruía-o ao sair. Com a bateria
   * real e a cadência real isso são dezenas de milhares de criações **e**
   * destruições por segundo, todas do mesmo objeto com a mesma configuração.
   *
   * **O pool não tem teto visual.** Se estiver vazio, cresce: um corpo nunca
   * deixa de ser desenhado por falta de objeto reciclado. A autoridade decide
   * quantos há, e a cena desenha todos.
   */
  private readonly shotPool: Phaser.GameObjects.Image[] = [];
  /**
   * As duas estruturas do laço, **reutilizadas** em vez de refeitas.
   *
   * `new Set` e `[...map.entries()]` a cada quadro eram duas coleções do
   * tamanho da população, vinte vezes por segundo. São limpas, não recriadas.
   */
  private readonly shotsAlive = new Set<number>();
  private readonly shotsGone: number[] = [];
  /** Os traços vivos que ficaram fora da vista neste quadro. */
  private readonly shotsCulledNow = new Set<number>();
  /**
   * **A cor de cada atirador neste quadro**, pela aparência que a sala publicou
   * (gate V28I.CF.3). Limpo e reescrito a cada quadro, nunca recriado.
   */
  private readonly shotColourByOwner = new Map<number, number>();
  /** A bitola do tiro no tamanho-base, calculada uma vez. */
  private readonly shotGauge = openWorldShotGauge();
  /** O alcance desenhado de um traço, por escala. Ver {@link OpenWorldScene.shotReach}. */
  private readonly shotReachByScale = new Map<number, number>();
  /**
   * **Um emissor de rasto por escala distinta**, criado quando é preciso.
   *
   * O rasto do duelo é um emissor só, com a rampa de espessura fixa, e uma rampa
   * não se escala tiro a tiro. Um emissor por escala é limitado pelo número de
   * pilotos à vista — cada casco tem uma escala —, e todos são destruídos no
   * fecho da cena.
   */
  private readonly trailEmitters = new Map<number, Phaser.GameObjects.Particles.ParticleEmitter>();
  /** O rumo anterior e a contagem até à próxima emissão, por tiro vivo. */
  private readonly shotWake = new Map<number, { heading: number; countdown: number }>();
  /** A cor com que cada traço vivo foi pintado: a textura só se troca quando ela muda. */
  private readonly shotColour = new Map<number, number>();
  /**
   * **A contabilidade da apresentação** (gate V28I.AJ), efémera e sem consumidor.
   *
   * Não altera mecânica, não entra na instantânea, não atravessa o protocolo,
   * não escreve por quadro e não aloca por corpo: são inteiros num objeto só.
   * O harness final lê-os por {@link shotVisualCounts}.
   */
  private readonly shotVisuals = {
    /** `Image` construídos por esta cena, desde sempre. */
    created: 0,
    /** Vezes que um corpo foi desenhado por um objeto reciclado. */
    reused: 0,
    /** Objetos guardados à espera de serem reutilizados. */
    pooled: 0,
    /** Corpos com objeto atribuído agora. */
    inUse: 0,
    /** Posições visuais aplicadas a um sprite. */
    moved: 0,
    /** Traços dentro da vista (com folga) no último quadro: todos com sprite. */
    inView: 0,
    /** Traços fora da vista, sem sprite, desde sempre (gate V28I.CG). */
    culled: 0,
    /**
     * **Objetos temporários criados pela extrapolação: zero, por construção.**
     *
     * A camada de apresentação move os números em pé e a cena só os lê — não há
     * caminho por onde um objeto temporário nasça, e este contador diz isso com
     * um número em vez de com uma promessa.
     */
    extrapolationGarbage: 0,
    /** O máximo que esta cena já teve em uso ao mesmo tempo. */
    peakInUse: 0,
    /** Emitters, tweens, timers e texturas que esta família cria: nenhum. */
    emitters: 0,
    tweens: 0,
    timers: 0,
    textures: 0,
  };
  private readonly pendingTextures = new Set<string>();
  /**
   * Os índices cuja arte foi recusada. Dito uma vez, e não a cada quadro.
   *
   * A chave é `familia:indice` porque a recusa é da folha, e um mapa pode ter
   * boss sem ter alienígena no dia em que uma das duas artes existir sem a outra.
   */
  private readonly refusedMapArt = new Set<string>();
  private cameraX = 0;
  private cameraY = 0;
  /**
   * **O cursor de apresentação** (gate V28I.BX.1): o tique fracionário, no
   * tempo da sala, em que tudo o que se move é desenhado neste quadro. Segue o
   * `k` das fotografias e nunca passa da mais recente — ver
   * `open-world/presentation.ts`. `OPEN_WORLD_TICK_MS` é `PVP_TICK_MS` por
   * definição, e é este o que o pacote exporta.
   */
  private readonly presentation = new OpenWorldPresentationCursor(PVP_TICK_MS);
  private presentTick: number | null = null;
  /** De quem é a vista. Mudar de identidade — travessia, reconexão — recomeça tudo. */
  private presentedMe = 0;
  /** As últimas posições autoritativas de cada casco, por tique. */
  private readonly tracks = new Map<number, OpenWorldTrack>();
  /** E de cada corpo NÃO linear: os lineares têm a aritmética da sala. */
  private readonly shotTracks = new Map<number, OpenWorldTrack>();
  /** Trilhas devolvidas, para uma nave ou um corpo novo não alocar. */
  private readonly trackPool: OpenWorldTrack[] = [];
  /** Quem veio na última fotografia: conjuntos reutilizados, esvaziados a cada uma. */
  private readonly seenHulls = new Set<number>();
  private readonly seenShots = new Set<number>();
  /** As libertações, ligadas uma vez: podar não aloca uma função por fotografia. */
  private readonly releaseHullTrack = (id: number): void => this.releaseTrack(this.tracks, id);
  private readonly releaseShotTrack = (id: number): void => this.releaseTrack(this.shotTracks, id);
  /** Onde se escreve uma posição apresentada: um objeto só, reutilizado. */
  private readonly drawn: OpenWorldPoint = { x: 0, y: 0 };
  /**
   * **O próprio casco, previsto** (gate V28I.BX.2): a sala levada até agora
   * pelo que o jogador está a segurar — ver `OpenWorldOwnHullPredictor`. Os
   * outros cascos continuam no cursor, interpolados.
   */
  private readonly ownHull = new OpenWorldOwnHullPredictor(
    PVP_TICK_MS,
    pvpWorldUnits(OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK),
  );
  /** Onde o ponteiro está no ecrã: a mira refaz-se a cada quadro a partir dele. */
  private pointerX: number | null = null;
  private pointerY = 0;
  private readonly aimPoint = new Phaser.Math.Vector2();
  /** A mira deste quadro, a mesma que sai: o nariz do próprio casco segue-a. */
  private ownAimX = 1;
  private ownAimY = 0;
  /** O instante deste quadro, no relógio de apresentação. Um valor, lido por todos. */
  private stepNow = 0;
  /** O presente dos corpos lineares do próprio casco. Ver `OpenWorldPresentationCursor.head`. */
  private headTick: number | null = null;
  /**
   * **De quem é cada corpo linear**, decidido uma vez, quando aparece: do casco
   * mais perto do sítio onde nasceu, no tique em que nasceu. O fio não diz o
   * dono de um tiro, e isto não o decide para nada além de QUANDO desenhar.
   */
  private readonly shotOwnership = new Map<number, boolean>();
  private readonly seenLinear = new Set<number>();
  private readonly probe: OpenWorldPoint = { x: 0, y: 0 };
  /**
   * **O zoom do jogador** (gate V28I.BX.1): multiplica o zoom responsivo e
   * nunca o substitui. 1 é a vista canónica — o afastamento máximo.
   */
  private playerZoomTarget = 1;
  private playerZoomShown = 1;
  /** O último frame autoritativo. Substituído por inteiro, nunca acumulado. */
  private frame: OpenWorldSceneFrame = NO_FRAME;
  private active = false;
  /**
   * **Os comandos locais valem?** (gate V28I.R).
   *
   * Falso enquanto uma camada de interface está aberta por cima. Nada disto
   * pausa a cena — ela continua a desenhar e a publicar um comando por tique,
   * apenas neutro — e nada disto protege a nave: ela fica parada, no meio do
   * quadrante, e continua a poder ser atingida.
   */
  private controlsEnabled = true;
  private readonly intent = new OpenWorldIntentSource();
  private readonly unsubscribe: (() => void)[] = [];

  constructor(private readonly options: OpenWorldSceneOptions) {
    /*
     * **ATIVA desde o arranque, a desenhar nada até lhe dizerem.**
     *
     * Nascer inativa obrigaria a um `scene.start` mais tarde, que é gestão de
     * cenas que este projeto evita deliberadamente — e foi exatamente o que
     * partiu esta cena na primeira inspeção: registada sem `active`, o
     * `create` nunca corria, portanto ela nunca subscrevia a ponte e nenhuma
     * mensagem de abertura chegava a lado nenhum.
     *
     * Uma cena dormente custa um `update` que devolve na primeira linha.
     */
    super({ key: OpenWorldScene.KEY, active: true });
  }

  preload(): void {
    /*
     * Nada de casco aqui. `preload` corre antes de o servidor dizer quem está
     * no quadrante, e qual nave cada um voa é resposta dele. O que se prepara é
     * o recurso: um disco branco que segura o lugar até a arte chegar.
     */
    if (!this.textures.exists(PLACEHOLDER)) {
      const graphics = this.make.graphics({ x: 0, y: 0 }, false);
      graphics.fillStyle(0x6fd7ff, 1);
      graphics.fillCircle(32, 32, 30);
      graphics.generateTexture(PLACEHOLDER, 64, 64);
      graphics.destroy();
    }
    /*
     * **Nenhum corpo de criatura aqui, e pela mesma razão que nenhum casco**
     * (gate V28I.BI).
     *
     * Até este gate carregavam-se duas folhas fixas, `alien1` e `boss1`, porque
     * o índice 1 era o único que se podia saber antes de o servidor falar. Isso
     * fazia os vinte alienígenas e os vinte bosses do mundo aberto serem
     * desenhados com a arte do mapa 1.
     *
     * A arte de um alienígena e de um boss é uma função do `mapIndex` deles, e o
     * `mapIndex` chega na trama. As folhas são pedidas quando a primeira
     * criatura daquele mapa aparece, por `requestOpenWorldMapBody`.
     */
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#03060f');
    this.starfield = new OpenWorldStarfield(this, -100);
    /*
     * A cena nasce **calada**. Ela existe desde que o jogo existe, como as
     * outras três, e só desenha quando o quadrante é aberto — o que é o que
     * torna abrir e fechar uma expedição uma coisa que não toca no canvas.
     *
     * `sys.setVisible` é a mesma alavanca que a caça usa para se esconder
     * durante um duelo: uma bandeira nos sistemas da cena, com a lista de
     * exibição, os objetos, o relógio e estas subscrições todos intactos.
     */
    this.sys.setVisible(false);

    const bridge = this.options.bridge;
    this.unsubscribe.push(
      bridge.onGameMessage('open-world:open', () => {
        this.active = true;
        this.controlsEnabled = true;
        this.sys.setVisible(true);
        this.intent.clear();
        /*
         * O céu é reconstruído quando falta. `release` destrói-o ao fechar a
         * expedição, e sem isto uma segunda expedição na mesma sessão abria
         * sobre um fundo preto vazio. Reconstruir e não guardar: um campo de
         * estrelas é uma função da câmara, e não tem nada para lembrar.
         */
        this.starfield ??= new OpenWorldStarfield(this, -100);
        this.cometArt ??= new OpenWorldCometArt(this, HULL_DEPTH - 1);
        /*
         * Um degrau abaixo das rochas: um planeta e maior e passa por tras
         * delas, e a fenda interna dele passa por baixo das proprias placas.
         */
        this.planetArt ??= new OpenWorldPlanetArt(this, HULL_DEPTH - 3);
        /*
         * Os mobs vivem no MESMO degrau dos cascos: sao coisas que se movem e
         * se enfrentam, e por-los por tras das rochas fa-los-ia desaparecer
         * atras de uma pedra que eles atravessam.
         */
        this.mobArt ??= new OpenWorldMobArt(this, HULL_DEPTH);
        /*
         * Um degrau ACIMA dos cascos: um boss é enorme e passa à frente de tudo
         * o resto, e a carga de uma magia dele tem de ser lida por cima da
         * confusão em vez de por baixo dela.
         */
        this.bossArt ??= new OpenWorldBossArt(this, HULL_DEPTH + 1);
        /*
         * **Um degrau ABAIXO do corpo**: um aviso desenha-se no chão, por baixo
         * de quem o vai apanhar e de quem o lança, e as brasas ficam mais abaixo
         * ainda. Um aviso por cima do casco tapava a nave que tem de fugir dele.
         */
        this.bossStrikeFx ??= new OpenWorldBossStrikeFx(this, HULL_DEPTH - 1);
        /*
         * **Por baixo de tudo**, e acima do céu: a água é um lugar, e um lugar
         * passa por trás das naves, dos tiros, das rochas e das criaturas que se
         * movem nele.
         */
        this.waterArt ??= new OpenWorldWaterArt(this, HULL_DEPTH - 6);
        this.merchantArt ??= new OpenWorldMerchantArt(this, HULL_DEPTH - 2);
        this.boxArt ??= new OpenWorldBoxArt(this, HULL_DEPTH - 3);
        this.cardBlasts ??= new CardBlastFx(this, Phaser.BlendModes.ADD, HULL_DEPTH + 3);
        /*
         * A MESMA arte do duelo, com a mesma onda partilhada: um segundo anel
         * idêntico com outra chave seria a mesma imagem paga duas vezes.
         */
        this.atomicBlasts ??= new AtomicBlastSystem(this as never, {
          depth: HULL_DEPTH + 4,
          waveTexture: ensureShockwaveTexture(this),
          blend: { add: Phaser.BlendModes.ADD, normal: Phaser.BlendModes.NORMAL },
        });
      }),
      bridge.onGameMessage('open-world:close', () => {
        this.active = false;
        this.sys.setVisible(false);
        this.frame = NO_FRAME;
        this.release();
      }),
      bridge.onGameMessage('open-world:frame', (payload) => {
        this.frame = payload;
        this.observeFrame(payload);
      }),
      /*
       * **Uma camada abriu-se por cima.** Larga tudo, e ignora o que vier.
       *
       * Nada aqui pausa a cena: ela continua a desenhar, a interpolar e a
       * publicar um comando por tique — apenas neutro. A sala não é informada,
       * porque não há nada que ela devesse fazer de diferente: a nave fica
       * parada e continua a poder ser atingida.
       */
      bridge.onGameMessage('open-world:controls', (payload) => {
        this.controlsEnabled = payload.enabled;
        this.intent.clear();
      }),
    );

    /*
     * **O `SHUTDOWN` do Phaser larga tudo, e CALA a cena.**
     *
     * `release` é o caminho normal: a expedição acaba, os objetos vão-se, as
     * subscrições ficam, e a expedição seguinte reabre nesta mesma cena. Este é
     * o outro caminho — o jogo inteiro a ser deitado abaixo — e aqui as
     * subscrições TÊM de cair com ele.
     *
     * Sem isto, uma cena destruída continuava inscrita na ponte, que sobrevive
     * ao jogo. A cena nova subscrevia por cima, e uma só mensagem de abertura
     * chegava às duas: a viva desenhava, e a morta tentava criar uma textura
     * nos seus sistemas já anulados e rebentava. Foi exatamente o que a
     * primeira execução da bancada mostrou, e é a razão por que "não duplicar
     * ao reconstruir a cena" tem de ser cumprido dos dois lados — nos objetos
     * e nos ouvintes.
     */
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.dispose());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.dispose());

    /*
     * **O teclado lido no próprio evento, e não na fila do Phaser** (gate
     * V28I.BX.2).
     *
     * O Phaser 4 guarda cada tecla numa fila e só a entrega no passo seguinte:
     * até um quadro inteiro entre premir e o comando sair. Um ouvinte na janela
     * — o mesmo alvo que o Phaser escuta — recebe-a no instante, e a transição
     * sai na hora. Um par por cena, retirado com as subscrições no `SHUTDOWN`.
     */
    const onKeyDown = (event: KeyboardEvent): void => {
      if (this.active && this.controlsEnabled && !event.repeat) {
        this.intent.press(event.code, event.timeStamp);
        this.publishIntent(event.timeStamp);
      }
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      this.intent.release(event.code, event.timeStamp);
      if (this.active) {
        this.publishIntent(event.timeStamp);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    this.unsubscribe.push(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    });
    /*
     * O rato já chega no próprio evento — o Phaser processa-o na hora — e o
     * começo e o fim de um disparo saem logo, como as teclas (gate V28I.BX.2).
     */
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.active && this.controlsEnabled) {
        const at = pointer.event?.timeStamp ?? performance.now();
        this.intent.setFiring(true, at);
        this.publishIntent(at);
      }
    });
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const at = pointer.event?.timeStamp ?? performance.now();
      this.intent.setFiring(false, at);
      if (this.active) {
        this.publishIntent(at);
      }
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      /*
       * Guarda-se onde o ponteiro está no ecrã, e a mira sai dele — agora e em
       * cada quadro a seguir, porque o casco anda com o rato parado. A mira
       * parte da posição DESENHADA, que é onde o jogador vê a nave: usar a
       * autoritativa apontaria de um sítio diferente daquele no ecrã.
       */
      this.pointerX = pointer.x;
      this.pointerY = pointer.y;
      this.refreshAim();
    });
    /*
     * **O zoom do jogador, pela roda** (gate V28I.BX.1).
     *
     * Só apresentação: muda o que a câmara mostra, e não a mira — que é
     * convertida por `getWorldPoint`, já com o zoom — nem a posição, o
     * alcance, a área de interesse, a colisão ou regra nenhuma. Com uma camada
     * aberta por cima, a roda não é deste mundo.
     */
    this.input.on(
      'wheel',
      (_pointer: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
        if (this.active && this.controlsEnabled) {
          /* A roda não se afasta para lá do envelope: numa janela grande o chão sobe. */
          const floor =
            openWorldEnvelopeZoomFor(this.scale.width, this.scale.height) /
            openWorldZoomFor(this.scale.width, this.scale.height);
          this.playerZoomTarget = Math.max(
            floor,
            openWorldPlayerZoomAfterWheel(this.playerZoomTarget, dy),
          );
        }
      },
    );
    /*
     * **O foco perdido larga tudo** (gate V28I.BX.1). Um par de ouvintes por
     * cena, registado uma vez e retirado com as subscrições no `SHUTDOWN`.
     */
    this.unsubscribe.push(
      releaseOpenWorldIntentOnFocusLoss(window, document, () => this.releaseHeldIntent()),
    );
    /*
     * **A ouvir, e di-lo** (gate V28I.BZ). Um F5 a meio de uma expedição sabe
     * que a tem de restaurar antes de o jogo existir; o React espera por isto
     * para a abrir, em vez de publicar para uma cena que ainda não subscreveu.
     */
    bridge.sendToUi('open-world:scene-ready', {});
  }

  override update(time: number, delta: number): void {
    if (!this.active) {
      return;
    }
    try {
      this.step(delta, time);
    } catch (error) {
      /*
       * Uma arena parada em silêncio nunca é um desfecho aceitável. O erro sobe
       * para React, que decide o que dizer ao jogador.
       */
      this.options.bridge.sendToUi('open-world:scene-error', {
        stage: 'update',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Um passo: ler a intenção, enviá-la, e desenhar o que o servidor disse.
   *
   * A ordem importa. O comando sai **antes** do desenho para que o que se vê já
   * seja a resposta ao que se acabou de pedir e não a resposta ao pedido
   * anterior.
   */
  private step(delta: number, frameTime: number): void {
    /*
     * **O instante do quadro, e não o de agora** (gate V28I.BX.2). É o carimbo
     * do `requestAnimationFrame`, no mesmo relógio de `performance.now()`, e
     * avança exatamente um quadro de cada vez: medido na bancada, o intervalo
     * por ele varia 0,4 %, e por `performance.now()` no passo variava 5 % —
     * o passo corre um pouco mais tarde ou mais cedo em cada quadro, e tudo o
     * que se move tremia com ele. Os gestos carimbam-se com o instante do
     * próprio evento, que é anterior ao do quadro que o trata.
     */
    const now = frameTime;
    this.stepNow = now;
    /*
     * A mira refaz-se a partir do ponteiro ANTES de sair: a câmara e o casco são
     * os do quadro que o jogador está a ver.
     */
    this.refreshAim();
    this.publishIntent(now);
    const frame = this.frame;
    /*
     * **Onde, no tempo da sala, se desenha este quadro** (gate V28I.BX.1). Um
     * valor por quadro, lido por todos os que desenham coisas que se movem. E
     * o presente, para os corpos lineares do próprio casco (gate V28I.BX.2).
     */
    this.presentTick = this.presentation.advance(now);
    this.headTick = this.presentation.head(now);
    /*
     * **As rochas antes dos cascos.** Uma nave passa por cima de um cometa, e
     * a ordem de desenho é o que o diz — as profundidades já o garantem, e esta
     * ordem mantém o mesmo facto legível na leitura do passo.
     */
    this.planetArt?.update(frame.planets, frame.planetEvents, this.time.now, pvpWorldUnits);
    /*
     * **A folha do mapa de cada criatura, pedida uma vez** (gate V28I.BI).
     *
     * Antes de desenhar, e não durante: quem desenha usa a chave que existir, e
     * quem pede é a cena — a mesma que já pede os cascos e os canhões, com o
     * mesmo conjunto de chaves em voo. As duas buscas por criatura são num mapa,
     * e a chamada devolve de imediato assim que a folha existe.
     */
    for (const mob of frame.mobs) {
      this.requestMapBody('mob', mob.mapIndex);
    }
    for (const boss of frame.bosses) {
      this.requestMapBody('boss', boss.mapIndex);
    }
    this.mobArt?.update(frame.mobs, frame.mobEvents, this.time.now, pvpWorldUnits);
    this.bossArt?.update(
      frame.bosses,
      /*
       * **O raio autoritativo, convertido pelas mesmas unidades de tudo o
       * resto** (gate V28I.Z.1). Ausente continua ausente: uma parcela da Lança
       * Solar não tem área, e o efeito dela não muda. E uma etapa do Bombardeio
       * de Profundidade vai sem o anel genérico (gate V28I.CF.1A).
       */
      openWorldBossEventsForArt(frame.bossEvents, frame.bossStrikes, pvpWorldUnits),
      this.time.now,
      pvpWorldUnits,
    );
    /*
     * **As esferas do enxame, extraídas dos corpos em voo** (gate V28I.BC).
     *
     * O buffer é do campo e não do quadro: ele é esvaziado e voltado a encher,
     * e a capacidade que ganhou fica. Nada é alocado aqui.
     */
    this.swarmBodies.length = 0;
    this.rotaryBodies.length = 0;
    this.shellBodies.length = 0;
    for (const shot of frame.shots) {
      if (shot.base.card === 'd') {
        /*
         * **Um obus, na posição de todos os corpos em voo** (gate V28I.CF.1A): a
         * mesma interpolação no cursor, e o destino que a sala disse na entrada.
         */
        this.presentShot(shot, this.drawn);
        this.shellBodies.push({
          id: shot.base.id,
          drawnX: this.drawn.x,
          drawnY: this.drawn.y,
          vx: shot.base.vx ?? 0,
          vy: shot.base.vy ?? 0,
          targetX: shot.base.targetX ?? shot.x,
          targetY: shot.base.targetY ?? shot.y,
        });
        continue;
      }
      if (shot.base.card === 'g') {
        this.rotaryBodies.push({
          id: shot.base.id,
          x: shot.x,
          y: shot.y,
          radiusMu: shot.base.blastMu ?? 0,
        });
        continue;
      }
      if (shot.base.card !== 'w') {
        continue;
      }
      this.swarmBodies.push({
        id: shot.base.id,
        x: shot.x,
        y: shot.y,
        radiusMu: shot.base.blastMu ?? 0,
      });
    }
    /*
     * **As ultimates em curso**, montadas a partir dos bosses da vista.
     *
     * O envelope de desenho precisa da posição e do corpo de quem canaliza, e
     * eles já estão no estado do boss: uma segunda lista na trama seria a mesma
     * informação duas vezes, com uma delas a poder ficar velha.
     */
    this.ultimateBodies.length = 0;
    for (const boss of frame.bosses) {
      const ultimate = boss.ultimate;
      if (ultimate === null) {
        continue;
      }
      this.ultimateBodies.push({
        id: boss.id,
        ability: ultimate.ability,
        phase: ultimate.phase,
        progressMilli: ultimate.progressMilli,
        interruptMilli: ultimate.interruptMilli,
        x: boss.x,
        y: boss.y,
        radiusMu: boss.radiusMu,
        innerMu: ultimate.innerMu,
        outerMu: ultimate.outerMu,
        involved: ultimate.involved,
      });
    }
    this.bossStrikeFx?.update(
      frame.bossStrikes,
      frame.bossEmbers,
      this.swarmBodies,
      this.rotaryBodies,
      this.shellBodies,
      frame.bossHoming,
      frame.bossCores,
      this.ultimateBodies,
      frame.tick,
      this.time.now,
      pvpWorldUnits,
    );
    const self = frame.entities.find((entity) => entity.id === frame.me) ?? null;
    this.merchantArt?.update(frame.merchant, this.time.now, pvpWorldUnits);
    this.boxArt?.update(
      frame.boxes,
      frame.nearestBox,
      this.time.now,
      pvpWorldUnits,
      /* Um baú é do tamanho de um casco: é uma coisa que uma nave largou. */
      PVP_HULL_WORLD_UNITS * 1.1,
    );
    this.waterArt?.update({
      waters: frame.waters,
      fishers: frame.fishers,
      own: frame.fishing,
      entities: frame.entities,
      selfX: self?.x ?? 0,
      selfY: self?.y ?? 0,
      nowMs: this.time.now,
      worldUnits: pvpWorldUnits,
    });
    this.cometArt?.update(frame.comets, frame.cometEvents, this.time.now, pvpWorldUnits);
    this.drawHulls(frame, delta);
    this.drawShots(frame);
    this.drawCards(frame, delta);
    this.syncProtection(frame);
    this.followCamera(frame, delta);
    /*
     * Cinco números soltos e não um literal: um objeto de câmara construído a
     * cada quadro seria uma alocação por quadro para transportar quatro
     * inteiros que já existem.
     *
     * As duas dimensões são a área VISÍVEL e não a tela (gate V28I.AN.1): uma
     * estrela é reciclada quando sai do que se vê, e com zoom acima de 1 vê-se
     * menos mundo do que há pixeis.
     */
    const camera = this.cameras.main;
    this.starfield?.update(
      camera.scrollX,
      camera.scrollY,
      this.scale.width / camera.zoom,
      this.scale.height / camera.zoom,
      this.time.now,
    );
  }

  /**
   * **O que as cartas desenham** (gate V28I.P).
   *
   * Quatro famílias, todas já aprovadas noutra arena e nenhuma delas escrita
   * aqui: os corpos e as explosões, o círculo do Turbilhão, o feixe da Lança, e
   * as duas invocações. O que este método faz é traduzir o estado autoritativo
   * deste mundo para o que elas já sabem receber.
   *
   * **Nada aqui decide.** Um corpo aparece porque a sala o publicou, uma
   * invocação desaparece porque a sala deixou de a publicar, e o feixe arde com
   * a força que a sala disse. A cena não sabe o que é dano, alcance nem
   * duração.
   */
  private drawCards(frame: OpenWorldSceneFrame, delta: number): void {
    /* As entidades que continuam na vista. O que sair leva os sinais consigo. */
    const alive = new Set<number>();
    /*
     * **Os corpos de carta**, pela mesma família do duelo.
     *
     * A idade não viaja — ela é do duelo, onde a esfera cresce a caminho — e
     * aqui o raio já é a verdade no instante em que ela nasce: a sala fixa-o na
     * soltura, com a força que a carga alcançou. `bodyRadius` presente é
     * exatamente isso: "não cresças, este é o tamanho".
     */
    const bodies: CardBlastBody[] = [];
    for (const shot of frame.shots) {
      if (shot.base.card === null) {
        continue;
      }
      if (shot.base.card === 'w' || shot.base.card === 'g' || shot.base.card === 'd') {
        /*
         * **Uma esfera de enxame NÃO é um corpo de carta** (gate V28I.BC).
         *
         * `CardBlastFx` é arte de carta, e este gate proíbe reutilizá-la para
         * uma magia de boss. Ela é desenhada por `OpenWorldBossStrikeFx`, com
         * as texturas próprias dele, e cada uma com o seu rasto e o seu
         * impacto — nunca agregada com outra.
         */
        continue;
      }
      const radius = shot.base.blastMu === null ? 0 : pvpWorldUnits(shot.base.blastMu);
      /* No mesmo tique que o sprite do corpo: a arte da carta e o corpo andam juntos. */
      this.presentShot(shot, this.drawn);
      bodies.push({
        id: shot.base.id,
        kind: CARD_BODY_KIND[shot.base.card],
        x: this.drawn.x,
        y: this.drawn.y,
        ageMs: 0,
        radius,
        bodyRadius: radius,
      });
    }
    this.cardBlasts?.update(bodies, this.time.now);
    /*
     * **As explosões, uma vez cada.**
     *
     * A deduplicação é de quem entrega: a sala drena a lista uma vez por
     * pulsação e cada ligação recebe cada explosão uma só vez. Uma segunda
     * memória aqui envelheceria ao lado da primeira.
     */
    for (const burst of frame.cardBursts) {
      this.cardBlasts?.burst(
        {
          id: burst.id,
          kind: burst.kind === 'c' ? 'catole' : 'fireball',
          x: pvpWorldUnits(burst.x),
          y: pvpWorldUnits(burst.y),
          /* O raio MECÂNICO: o desenho abre exatamente até onde ele feriu. */
          radius: pvpWorldUnits(burst.radiusMu),
        },
        this.time.now,
      );
    }
    for (const blast of frame.blasts) {
      this.atomicBlasts?.spawn({
        id: blast.id,
        x: pvpWorldUnits(blast.x),
        y: pvpWorldUnits(blast.y),
        radius: pvpWorldUnits(blast.radiusMu),
      });
    }

    /* --- o círculo do Turbilhão e o feixe da Lança, por casco --- */
    const withCircle = new Set<number>();
    const withLance = new Set<number>();
    for (const entity of frame.entities) {
      const view = this.hulls.get(entity.id);
      if (view === undefined) {
        continue;
      }
      const flags = cardVisualFlags(entity.cards.visuals);
      const heading = Math.atan2(entity.aimY, entity.aimX);
      const hullRadius = PVP_HULL_WORLD_UNITS / 2;
      /*
       * **Os sinais que descrevem o CASCO**: o que lhe está a ser feito, o que
       * ele está a fazer a si próprio, e os dois pulsos de quem lança. Todos
       * pelas mesmas famílias do duelo, e todos a partir do que a sala disse.
       */
      this.drawCardSignals(entity, view, flags, heading, hullRadius, delta);
      alive.add(entity.id);
      /* O CÍRCULO e a SERRA, exatamente como o duelo os desenha. */
      if (flags.circle) {
        withCircle.add(entity.id);
        const circle =
          this.cardCircles.get(entity.id) ??
          new MagicCircleFx(this, Phaser.BlendModes.ADD, HULL_DEPTH - 1);
        this.cardCircles.set(entity.id, circle);
        circle.update(
          {
            x: view.drawX,
            y: view.drawY,
            /*
             * A mesma proporção com que a sala decide quem a serra apanha — o
             * alcance é basis points do raio do casco. Um número escolhido aqui
             * seria um círculo a prometer uma área que não é a que fere.
             */
            radius: bladeOrbitRadius(PVP_HULL_WORLD_UNITS, CARD_BALANCE.card_blade_storm.reachBps),
            active: flags.blade,
            tint: cardEffectTint(),
          },
          this.time.now,
        );
      }
      /*
       * **A ESFERA a carregar, à frente da boca.**
       *
       * O raio é o que ela TERÁ se soltar neste instante — a mesma curva que a
       * sala usa ao lançar, sobre o mesmo alcance — portanto a bola que cresce
       * é a bola que sai, e quem está à frente pode julgar o que vem na
       * direção dele.
       */
      if (flags.sphere) {
        const sphere = chargingSphereGeometry({
          chargePercent: entity.cards.chargePercent ?? undefined,
          x: view.drawX,
          y: view.drawY,
          heading,
          hullRadius,
          reachBps: PVP_FIREBALL_REACH_BPS,
          radiusFor: cardSphereRadiusFor,
          bodyFraction: CARD_FIREBALL_BODY_FRACTION,
        });
        if (sphere !== null) {
          this.chargingSpheres.push({ key: String(entity.id), ...sphere });
        }
      }
      /* A LANÇA: a carga, o feixe, e a dissipação depois dele. */
      if (flags.beam || flags.charging || this.cardLances.has(entity.id)) {
        withLance.add(entity.id);
        const lance =
          this.cardLances.get(entity.id) ??
          new SolarLanceFx(this, Phaser.BlendModes.ADD, HULL_DEPTH + 2);
        this.cardLances.set(entity.id, lance);
        /*
         * A BOCA, e não o centro do casco: é de lá que a sala mede o feixe, e
         * desenhá-lo do centro punha-o por cima da nave.
         */
        const muzzleX = view.drawX + Math.cos(heading) * hullRadius;
        const muzzleY = view.drawY + Math.sin(heading) * hullRadius;
        const strengthBps = ((entity.cards.beamPercent ?? 100) * 10_000) / 100;
        lance.update(
          {
            charge: flags.charging
              ? { x: muzzleX, y: muzzleY, progress: (entity.cards.chargePercent ?? 0) / 100 }
              : null,
            beam: flags.beam
              ? {
                  x: muzzleX,
                  y: muzzleY,
                  angle: heading,
                  length: (hullRadius * PVP_SOLAR_LANCE_REACH_BPS) / 10_000,
                  /* A espessura TOTAL, graduada pela carga que a sala cobra. */
                  width:
                    cardBeamWidthFor(
                      (hullRadius * PVP_SOLAR_LANCE_WIDTH_BPS) / 10_000,
                      strengthBps,
                    ) * 2,
                }
              : null,
            hullRadius,
          },
          this.time.now,
        );
      }
    }
    /*
     * **As esferas a carregar, entregues de uma vez.** A família mantém uma
     * por chave e apaga as que deixarem de vir, portanto uma nave que solta a
     * tecla perde a bola no mesmo quadro.
     */
    this.cardBlasts?.charging(this.chargingSpheres, this.time.now);
    this.chargingSpheres.length = 0;

    for (const [id, fx] of [...this.cardCircles.entries()]) {
      if (withCircle.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardCircles.delete(id);
    }
    for (const [id, fx] of [...this.cardLances.entries()]) {
      if (withLance.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardLances.delete(id);
    }

    /*
     * **O que sai da vista leva os sinais consigo.**
     *
     * Não há desvanecimento tardio: quando o estado autoritativo desaparece, o
     * sinal desaparece. Um selo que sobrevivesse ao casco seria uma coisa a
     * flutuar sobre nada, e um que se apagasse devagar estaria a afirmar um
     * estado que já não é verdade.
     */
    this.forgetCardSignals(alive);
    this.drawCreatureSignals(frame);
    this.drawSummons(frame);
  }

  /**
   * **As penalidades desenhadas sobre alienígenas e chefes.**
   *
   * A MESMA família de sinais dos cascos, apontada a outro corpo: um
   * alienígena lento leva o mesmo sinal que uma nave lenta leva, porque é o
   * mesmo estado. Nenhuma arte nova foi escrita — o que muda é o raio, que sai
   * do descritor autoritativo da criatura.
   *
   * As de BENEFÍCIO não aparecem, e não é uma omissão: uma criatura não lança
   * cartas, e o fio não tem sequer campo para lhas dizer.
   */
  private drawCreatureSignals(frame: OpenWorldSceneFrame): void {
    const alive = new Set<string>();
    const paint = (body: {
      readonly id: string;
      readonly x: number;
      readonly y: number;
      readonly radiusMu: number;
      readonly cards: OpenWorldCreatureCards;
    }): void => {
      const cards = body.cards;
      const anything =
        cards.frozen ||
        cards.slowed ||
        cards.paralysed ||
        cards.attackBroken ||
        cards.marked ||
        cards.frostTier > 0;
      const existing = this.creatureStatus.get(body.id);
      if (!anything && existing === undefined) {
        return;
      }
      alive.add(body.id);
      const fx =
        existing ??
        new StatusFx(this, Phaser.BlendModes.ADD, HULL_DEPTH + 2, HULL_DEPTH + 3, HULL_DEPTH - 4);
      this.creatureStatus.set(body.id, fx);
      const radius = pvpWorldUnits(body.radiusMu);
      fx.update(
        {
          x: pvpWorldUnits(body.x),
          y: pvpWorldUnits(body.y),
          hullRadius: radius,
          /* Acima da barra de vida da criatura, que é o que já lá está. */
          lift: radius + BAR_LIFT_UNITS,
          nameHeight: 0,
          /*
           * **O Malware não alcança uma criatura**, e por isso o selo dele não
           * é desenhado: ela não tem caminho de cura, e um selo que dissesse o
           * contrário seria a cena a afirmar uma coisa que a sala não decidiu.
           */
          malware: false,
          frostTier: cards.frostTier,
          paralysed: cards.paralysed,
          slowed: cards.slowed,
          attackBroken: cards.attackBroken,
          marked: cards.marked,
        },
        this.time.now,
      );
    };
    for (const mob of frame.mobs) {
      paint(mob);
    }
    for (const boss of frame.bosses) {
      paint(boss);
    }
    for (const [id, fx] of [...this.creatureStatus.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.creatureStatus.delete(id);
    }
  }

  /**
   * **Tudo o que uma carta desenha SOBRE um casco.**
   *
   * Seis famílias, todas aprovadas no duelo e nenhuma escrita aqui:
   *
   * - `StatusFx` — o selo e o círculo do Malware, a casca e os cristais do
   *   Congelamento, os flocos, a estrela da paralisia total, o sinal da Lentidão,
   *   o do Curto-Circuito e a Marca da Caveira;
   * - `BastionFx` — as estrelas e o brilho da invulnerabilidade;
   * - `MovementFx` — o rasto do Arranque e as turbinas da Sobremarcha;
   * - `MalwareCastFx` e `MarkCastFx` — os dois pulsos de QUEM lança;
   * - um anel de gelo, para o casco congelado;
   * - a etiqueta do Manto, e o casco a esbater-se.
   *
   * **Nada aqui decide.** Cada sinal existe porque a sala o publicou, e
   * desaparece porque ela deixou de o publicar.
   */
  private drawCardSignals(
    entity: OpenWorldSceneEntity,
    view: HullView,
    flags: ReturnType<typeof cardVisualFlags>,
    heading: number,
    hullRadius: number,
    delta: number,
  ): void {
    const cards = entity.cards;
    const x = view.drawX;
    const y = view.drawY;
    /*
     * O levantamento da barra, para o selo e a estrela ficarem acima dela. Esta
     * cena não desenha um nickname, portanto não há nome a saltar por cima.
     */
    const lift = pvpWorldUnits(entity.radiusMu) + BAR_LIFT_UNITS;

    /* --- os seis sinais de debuff, e o pulso de quem lança --- */
    const anyStatus =
      cards.malware ||
      cards.frostTier > 0 ||
      cards.paralysed ||
      cards.slowed ||
      cards.attackBroken ||
      cards.marked;
    const status = this.cardStatus.get(entity.id);
    if (anyStatus || status !== undefined) {
      const fx =
        status ??
        new StatusFx(
          this,
          Phaser.BlendModes.ADD,
          HULL_DEPTH + 2,
          HULL_DEPTH + 3,
          /*
           * O CHÃO: por baixo do casco e do que voa. Um selo deitado na arena
           * que passasse por cima do que está pousado nele ler-se-ia como um
           * autocolante na lente, e não como uma coisa que está no mundo.
           */
          HULL_DEPTH - 4,
        );
      this.cardStatus.set(entity.id, fx);
      fx.update(
        {
          x,
          y,
          hullRadius,
          lift,
          nameHeight: 0,
          malware: cards.malware,
          frostTier: cards.frostTier,
          paralysed: cards.paralysed,
          slowed: cards.slowed,
          attackBroken: cards.attackBroken,
          marked: cards.marked,
        },
        this.time.now,
      );
    }

    /* --- o pulso do MALWARE e o da MARCA, no casco de quem lançou --- */
    const marking = flags.markCast;
    const mark = this.cardMarks.get(entity.id);
    if (marking || mark !== undefined) {
      const stamp = mark ?? new MarkCastFx(this, HULL_DEPTH + 2);
      this.cardMarks.set(entity.id, stamp);
      /*
       * O ESTADO da carta, e não uma ordem: a letra fica acesa os segundos do
       * debuff, e é o flanco que arranca o pulso — portanto ele toca uma vez por
       * lançamento em vez de durante seis segundos.
       */
      stamp.sync(marking, this.time.now);
      stamp.update(x, y, hullRadius, this.time.now);
    }
    const casting = flags.malwareCast;
    const cast = this.cardMalware.get(entity.id);
    if (casting || cast !== undefined) {
      const pulse = cast ?? new MalwareCastFx(this, HULL_DEPTH + 2);
      this.cardMalware.set(entity.id, pulse);
      if (casting) {
        pulse.fire(this.time.now);
      }
      pulse.update(x, y, hullRadius, this.time.now);
    }

    /* --- o BASTIÃO: as estrelas, o brilho e a alternância aprovados --- */
    const bastion = this.cardBastions.get(entity.id);
    if (cards.untargetable) {
      const fx = bastion ?? new BastionFx(this, Phaser.BlendModes.ADD, HULL_DEPTH + 2);
      this.cardBastions.set(entity.id, fx);
      fx.update({ x, y, hullRadius: pvpWorldUnits(entity.radiusMu) }, this.time.now);
    } else {
      bastion?.update(null, this.time.now);
    }

    /* --- o ARRANQUE e a SOBREMARCHA --- */
    const movement = this.cardMovement.get(entity.id);
    if (flags.dash || flags.haste) {
      const fx = movement ?? new MovementFx(this, Phaser.BlendModes.ADD, HULL_DEPTH - 4);
      this.cardMovement.set(entity.id, fx);
      fx.update(
        {
          x,
          y,
          heading,
          hullRadius,
          haste: flags.haste,
          dash: flags.dash,
          /* A cor é de quem OLHA, como em todos os efeitos de carta. */
          tint: cardEffectTint(),
        },
        this.time.now,
        /* O tempo do QUADRO, para as riscas andarem por segundo e não por quadro. */
        delta,
      );
    } else {
      movement?.update(null, this.time.now, delta);
    }

    /*
     * --- o anel de GELO ---
     *
     * Só o gelo: o Bastião tem a composição dele, e dois anéis sobrepostos no
     * mesmo casco seriam exatamente o que a direção de arte proíbe.
     */
    const aura = this.cardAuras.get(entity.id);
    if (cards.frozen) {
      const radius = pvpWorldUnits(entity.radiusMu) * 1.24;
      const ring =
        aura ??
        this.add
          .circle(x, y, radius)
          .setStrokeStyle(3, 0x7fd8ff, 0.95)
          .setFillStyle()
          .setDepth(HULL_DEPTH + 2);
      this.cardAuras.set(entity.id, ring);
      ring.setVisible(true).setPosition(x, y).setRadius(radius);
    } else {
      aura?.setVisible(false);
    }

    /* --- o MANTO: o casco esbate-se, e diz-se por palavras --- */
    view.container.setAlpha(entity.connected ? (flags.cloaked ? CLOAK_HULL_ALPHA : 1) : 0.55);
    const tag = this.cloakTags.get(entity.id);
    if (flags.cloaked) {
      const label =
        tag ??
        this.add
          .text(x, y - lift - CLOAK_TAG_LIFT, 'INVISÍVEL', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '12px',
            color: '#bfe9ff',
            stroke: '#04060e',
            strokeThickness: 3,
          })
          .setOrigin(0.5, 1)
          .setDepth(HULL_DEPTH + 3);
      this.cloakTags.set(entity.id, label);
      label.setVisible(true).setPosition(x, y - lift - CLOAK_TAG_LIFT);
    } else {
      tag?.setVisible(false);
    }

    /* --- os dois CANHÕES temporários --- */
    this.drawSuperCannons(entity, view, heading);
  }

  /**
   * **Os dois canhões temporários, montados fora da nave.**
   *
   * A geometria é a NEUTRA — a mesma que a sala usa para decidir de onde os
   * tiros saem — e a arte é a do canhão que este casco já tem equipado: um
   * canhão temporário é o mesmo cano, montado noutro sítio. Uma segunda textura
   * seria arte nova, que não é deste gate.
   *
   * Escondidos em vez de destruídos: o ciclo desta carta é de seis segundos, e
   * criar e destruir dois sprites por ativação seria trabalho por ativação.
   */
  private drawSuperCannons(entity: OpenWorldSceneEntity, view: HullView, heading: number): void {
    const existing = this.superCannons.get(entity.id) ?? null;
    if (!entity.cards.superCannons) {
      for (const sprite of existing ?? []) {
        sprite.setVisible(false);
      }
      return;
    }
    const texture = view.cannons[0]?.texture.key ?? null;
    if (texture === null) {
      /* Sem canhão desenhado não há textura a reutilizar, e não se inventa uma. */
      return;
    }
    const sprites =
      existing ??
      (() => {
        const size = PVP_CANNON_WORLD_UNITS * 2;
        const created = [0, 1].map(() =>
          /*
           * ATRÁS do casco, como no duelo: eles montam fora da nave, e uma
           * profundidade menor mantém o casco a ler-se por cima de uma peça
           * temporária.
           */
          this.add
            .image(0, 0, texture)
            .setDisplaySize(size, size)
            .setDepth(HULL_DEPTH - 1),
        );
        this.superCannons.set(entity.id, created);
        return created;
      })();
    /*
     * A MESMA geometria que a sala usa, sobre a posição DESENHADA: o que se vê
     * é onde os tiros nascem, e dois cálculos seriam duas respostas.
     */
    const places = cardSuperCannonBodies(
      { x: view.drawX, y: view.drawY, aimX: entity.aimX, aimY: entity.aimY },
      pvpWorldUnits(entity.radiusMu),
    );
    for (const [index, sprite] of sprites.entries()) {
      const place = places[index];
      if (place === undefined) {
        continue;
      }
      sprite
        .setVisible(true)
        .setPosition(place.x, place.y)
        .setRotation(pvpCannonSpriteRotation(pvpCannonPivotAngle(heading, heading)));
    }
  }

  /**
   * **Larga os sinais de quem já não está na vista.**
   *
   * Uma travessia, uma correção, uma reconexão e uma morte chegam todas por
   * aqui: em qualquer delas a entidade deixa de vir na trama, e o que a
   * descrevia vai com ela. Não há persistência inventada.
   */
  private forgetCardSignals(alive: ReadonlySet<number>): void {
    for (const [id, fx] of [...this.cardStatus.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardStatus.delete(id);
    }
    for (const [id, fx] of [...this.cardMovement.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardMovement.delete(id);
    }
    for (const [id, fx] of [...this.cardMalware.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardMalware.delete(id);
    }
    for (const [id, fx] of [...this.cardMarks.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardMarks.delete(id);
    }
    for (const [id, fx] of [...this.cardBastions.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      fx.destroy();
      this.cardBastions.delete(id);
    }
    for (const [id, ring] of [...this.cardAuras.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      ring.destroy();
      this.cardAuras.delete(id);
    }
    for (const [id, tag] of [...this.cloakTags.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      tag.destroy();
      this.cloakTags.delete(id);
    }
    for (const [id, sprites] of [...this.superCannons.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      for (const sprite of sprites) {
        sprite.destroy();
      }
      this.superCannons.delete(id);
    }
  }

  /**
   * **O Drone e o Leviatã**, pela família neutra.
   *
   * Um contentor por identidade. Uma invocação que saia da lista é destruída e
   * não escondida: ao contrário de uma lâmina, ela não volta — a entidade é
   * irrepetível.
   */
  private drawSummons(frame: OpenWorldSceneFrame): void {
    const present = new Set<number>();
    let offer = false;
    const self = frame.entities.find((entity) => entity.id === frame.me) ?? null;
    for (const summon of frame.summons) {
      present.add(summon.id);
      if (summon.kind === 'd') {
        requestDroneTexture(this, this.options.manifest, this.pendingTextures);
      } else {
        requestLeviathanTexture(this, this.options.manifest, this.pendingTextures);
      }
      const diameter = summonDiameter(
        summon.kind,
        PVP_HULL_WORLD_UNITS,
        PVP_HULL_WORLD_UNITS * LEVIATHAN_MIN_HULLS,
      );
      const wanted = summonBuiltAs(this, summon.kind);
      let container = this.summons.get(summon.id);
      if (container !== undefined && this.summonBuilt.get(summon.id) !== wanted) {
        /* A arte chegou depois da forma: o contentor é refeito, e uma vez só. */
        container.destroy(true);
        this.summons.delete(summon.id);
        container = undefined;
      }
      if (container === undefined) {
        container = buildSummonBody(this, {
          kind: summon.kind,
          diameter,
          /*
           * A cor do LADO: a minha PT em ciano, e toda a gente em vermelho.
           * Num quadrante não há duas equipas — há a minha expedição e o resto
           * do mundo — e é isso que a cor diz.
           */
          tint: summon.ownerId === frame.me ? 0x63d2ff : 0xff8a6b,
          hullDepth: HULL_DEPTH,
          cannonDepth: HULL_DEPTH + 1,
        });
        this.summons.set(summon.id, container);
        this.summonBuilt.set(summon.id, wanted);
      }
      container.setPosition(pvpWorldUnits(summon.x), pvpWorldUnits(summon.y));
      container.setRotation(Math.atan2(summon.aimY, summon.aimX));
      setSummonHealth(container, summon.healthBps);
      spinSummonRotor(container, this.time.now);
      setSummonBoardingArrows(container, summon.pilotId === null, this.time.now);
      /*
       * **Estou encostado a uma estrutura VAZIA?**
       *
       * A cena responde porque é ela que tem as posições desenhadas, e a sala
       * REVALIDA no tique em que a ordem chega: o botão é um convite, e nunca
       * uma decisão. É exatamente a mesma repartição que o duelo já aprovou.
       */
      if (
        summon.kind === 'l' &&
        summon.pilotId === null &&
        self !== null &&
        Math.hypot(summon.x - self.x, summon.y - self.y) <=
          /*
           * O MESMO número que a sala usa para aceitar a entrada. Um convite
           * que aparecesse antes de ela aceitar seria um botão que não funciona.
           */
          (self.radiusMu * CARD_LEVIATHAN_ENTRY_BPS) / 10_000
      ) {
        offer = true;
      }
    }
    for (const [id, container] of [...this.summons.entries()]) {
      if (present.has(id)) {
        continue;
      }
      container.destroy(true);
      this.summons.delete(id);
      this.summonBuilt.delete(id);
    }
    if (offer !== this.boardOffer) {
      this.boardOffer = offer;
      this.options.bridge.sendToUi('open-world:board-offer', { available: offer });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Cascos                                                             */
  /* ------------------------------------------------------------------ */

  private drawHulls(frame: OpenWorldSceneFrame, _delta: number): void {
    const alive = new Set<number>();
    for (const entity of frame.entities) {
      alive.add(entity.id);
      const view = this.hullFor(entity);
      /*
       * **A posição desenhada é a da sala, no tique do cursor** (gate V28I.BX.1).
       *
       * Até aqui ela perseguia a recebida por uma fração do que faltava — mas o
       * alvo só mudava quando chegava uma fotografia, e a nave arrancava a cada
       * uma e travava antes da seguinte: medido na janela real, o passo por
       * quadro variava 177% com o rato parado e 42% com ele a mexer. Agora é
       * interpolada entre as duas amostras autoritativas que rodeiam o cursor:
       * a mesma velocidade em todos os quadros, e nunca uma posição para lá da
       * última que a sala disse.
       *
       * Sem trilha ainda — o primeiro quadro de um casco — desenha-se a posição
       * recebida, exatamente.
       */
      const track = this.tracks.get(entity.id);
      const presented = this.presentTick;
      /*
       * **O PRÓPRIO casco é previsto, e só ele** (gate V28I.BX.2): a sala levada
       * até agora pelo que o jogador está a segurar. Os outros continuam no
       * cursor, interpolados entre duas fotografias.
       */
      const own = entity.id === frame.me;
      if (own && this.ownHull.sample(this.stepNow, this.drawn)) {
        view.drawX = this.drawn.x;
        view.drawY = this.drawn.y;
      } else if (presented !== null && track?.sampleAt(presented, this.drawn)) {
        view.drawX = this.drawn.x;
        view.drawY = this.drawn.y;
      } else {
        view.drawX = pvpWorldUnits(entity.x);
        view.drawY = pvpWorldUnits(entity.y);
      }
      view.container.setPosition(view.drawX, view.drawY);

      /*
       * **O nariz aponta para onde a mira aponta.** O ângulo é o da mira
       * autoritativa; a rotação do sprite sai de `pvpHullRotation`, que é a
       * mesma função que orienta um casco num duelo — a arte não olha para o
       * mesmo lado que a matemática, e quem sabe disso é aquele ficheiro.
       */
      /*
       * O próprio nariz segue a mira LOCAL, a mesma que acabou de sair: o
       * ponteiro não espera pela sala para rodar a nave (gate V28I.BX.2).
       */
      const heading = own
        ? Math.atan2(this.ownAimY, this.ownAimX)
        : Math.atan2(entity.aimY, entity.aimX);
      view.hull.setRotation(pvpHullRotation(heading));
      const appearance = entity.appearance;
      if (appearance !== null) {
        const mounts = pvpCannonMounts(appearance);
        for (let index = 0; index < view.cannons.length; index += 1) {
          const cannon = view.cannons[index];
          const mount = mounts[index];
          if (cannon === undefined || mount === undefined) {
            continue;
          }
          const scale = pvpVisualScale(BigInt(entity.maxHealthMilli));
          const placed = pvpMountInWorld(mount, 0, 0, heading, scale);
          cannon.setPosition(placed.x, placed.y);
          const pivot = pvpCannonPivotAngle(heading, heading);
          cannon.setRotation(pvpCannonSpriteRotation(pivot));
        }
      }
      view.container.setAlpha(entity.connected ? 1 : 0.55);
      this.drawBar(view, entity);
      this.drawAllyRing(entity, view, frame.me);
    }
    for (const [id, view] of [...this.hulls.entries()]) {
      if (alive.has(id)) {
        continue;
      }
      view.container.destroy(true);
      this.hulls.delete(id);
      /* O aro sai com a nave: um aro sobre nada seria um aliado que não existe. */
      this.allyRings.get(id)?.destroy();
      this.allyRings.delete(id);
      /* E a trilha também: nenhuma posição de um casco que saiu sobrevive. */
      this.releaseTrack(this.tracks, id);
    }
  }

  /**
   * **O aro de um aliado**, por baixo da nave e discreto.
   *
   * O tamanho sai do raio AUTORITATIVO do casco, e não da largura do sprite: uma
   * nave com arte maior não ganha um aro maior do que a área que ela ocupa.
   *
   * Um inimigo não recebe nada, e a ausência é a diferença: marcar toda a gente
   * e distinguir pela cor seria pedir a quem não distingue ciano de vermelho que
   * adivinhasse quem lhe vai dar um tiro.
   */
  private drawAllyRing(entity: OpenWorldSceneEntity, view: HullView, meId: number): void {
    const existing = this.allyRings.get(entity.id) ?? null;
    /*
     * **Nunca no próprio casco** (gate V28I.BX.1). A sala manda o casco de quem
     * recebe sempre como aliado, e o aro desenhava-se à volta da nave de quem
     * joga — o círculo cinzento permanente que o dono mandou retirar. Ver
     * `openWorldDrawsAllyRing`.
     */
    if (!openWorldDrawsAllyRing(entity, meId)) {
      existing?.setVisible(false);
      return;
    }
    const ring =
      existing ??
      this.add
        .image(0, 0, ensureAllyRingTexture(this))
        .setDepth(HULL_DEPTH - 2)
        .setBlendMode(Phaser.BlendModes.ADD);
    this.allyRings.set(entity.id, ring);
    const diameter = pvpWorldUnits(entity.radiusMu) * 2.6;
    ring.setVisible(true).setPosition(view.drawX, view.drawY).setDisplaySize(diameter, diameter);
  }

  /**
   * A barra de vida: vermelha, com a largura da fração que resta.
   *
   * Desenhada por entidade e destruída com ela, que é a mesma disciplina do
   * duelo: uma barra que sobrevivesse à nave ficaria a flutuar sobre nada.
   */
  private drawBar(view: HullView, entity: OpenWorldSceneEntity): void {
    const max = BigInt(entity.maxHealthMilli);
    const current = BigInt(entity.healthMilli);
    const fraction = max <= 0n ? 0 : Number((current * 1000n) / max) / 1000;
    const width = PVP_HULL_WORLD_UNITS * 1.2;
    const radius = pvpWorldUnits(entity.radiusMu);
    view.bar.clear();
    view.bar.fillStyle(0x1a0a0f, 0.75);
    view.bar.fillRect(-width / 2, -radius - 10, width, 4);
    view.bar.fillStyle(0xff4d5e, 1);
    view.bar.fillRect(-width / 2, -radius - 10, width * Math.max(0, Math.min(1, fraction)), 4);
  }

  private hullFor(entity: OpenWorldSceneEntity): HullView {
    const existing = this.hulls.get(entity.id);
    /*
     * **A ESCALA entra na assinatura — e não a vida** (gate V28I.CF.3).
     *
     * O tamanho do casco e o dos canhões saem desta escala, e sem ela aqui uma
     * vista construída a `1,00x` era reutilizada para a mesma entidade com outra
     * vida: o casco e os canhões ficavam presos no tamanho antigo enquanto as
     * bocas e os tiros — que são recalculados por quadro e pela trama —
     * cresciam. Medido: 52 x 52 px em `1x`, `2x`, `5x` e `10x`, com a montagem a
     * ir de 41 a 412 px no mesmo intervalo.
     *
     * **É a escala, e nunca `maxHealthMilli` em cru.** Duas vidas diferentes que
     * produzam o mesmo tamanho partilham a vista em vez de a reconstruírem por
     * nada — e no dia em que a lei da escala ganhar um teto, a assinatura ganha-o
     * junto, porque é o MESMO número que dimensiona o sprite e que a compõe.
     * Inteira em basis points: um `number` aqui poria duas máquinas a discordar
     * no último bit e a reconstruir cascos uma da outra.
     *
     * É a MESMA disciplina do duelo, que já põe a escala do casco na dele.
     */
    const scaleBps = pvpHullScaleBps({ maxHealthMilli: BigInt(entity.maxHealthMilli) });
    const visualScale = hullScaleFactor(scaleBps);
    const signature = `${entity.appearance === null ? 'plain' : JSON.stringify(entity.appearance)}/${scaleBps}`;
    if (existing !== undefined && existing.builtFrom === signature) {
      return existing;
    }
    if (existing !== undefined) {
      existing.container.destroy(true);
      this.hulls.delete(entity.id);
    }

    const container = this.add.container(pvpWorldUnits(entity.x), pvpWorldUnits(entity.y));
    container.setDepth(HULL_DEPTH);
    const appearance = entity.appearance;
    const art = appearance === null ? null : pvpShipArt(appearance);
    if (art !== null) {
      this.requestTexture(art.key, art.assetId);
    }
    const hull = this.add.image(
      0,
      0,
      art !== null && this.textures.exists(art.key) ? art.key : PLACEHOLDER,
    );
    /*
     * **A escala aprovada do PvP, chamada e nunca recalculada.** Uma nave de
     * dez mil de vida mede aqui exatamente o que mede num duelo, porque é a
     * mesma função a dizê-lo.
     */
    const scale = pvpSpriteScale(hull.width, PVP_HULL_WORLD_UNITS, visualScale);
    hull.setScale(scale);
    container.add(hull);

    const cannons: Phaser.GameObjects.Image[] = [];
    if (appearance !== null) {
      const cannonArt = pvpCannonArt(appearance);
      if (cannonArt !== null) {
        this.requestTexture(cannonArt.key, cannonArt.assetId);
        for (const _mount of pvpCannonMounts(appearance)) {
          const sprite = this.add.image(
            0,
            0,
            this.textures.exists(cannonArt.key) ? cannonArt.key : PLACEHOLDER,
          );
          sprite.setScale(pvpSpriteScale(sprite.width, PVP_CANNON_WORLD_UNITS, visualScale));
          container.add(sprite);
          cannons.push(sprite);
        }
      }
    }

    const bar = this.add.graphics();
    container.add(bar);

    const view: HullView = {
      container,
      hull,
      cannons,
      bar,
      drawX: pvpWorldUnits(entity.x),
      drawY: pvpWorldUnits(entity.y),
      builtFrom: signature,
    };
    this.hulls.set(entity.id, view);
    return view;
  }

  /* ------------------------------------------------------------------ */
  /*  Tiros                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * **Projéteis discretos, e nunca outra coisa.**
   *
   * Um sprite por tiro, e é permanente: nada nesta cena junta uma sequência de
   * tiros num feixe, seja qual for a cadência. Sobreposição a cadências altas é
   * esperada e está bem.
   *
   * ## O tiro primário é o tiro do jogo (gate V28I.CF.3)
   *
   * Um tiro primário desenha-se com a arte final do duelo — o traço néon da
   * caça na cor do canhão de quem disparou, com a bitola e o aumento de cadência
   * do duelo, virado pela velocidade que a sala publicou e à escala física que a
   * sala congelou no disparo. Ver `open-world-shot-art.ts`.
   *
   * Tudo o resto — cartas, esferas de boss, bombas e mísseis — continua com o
   * desenho que tinha. Este gate não lhes toca.
   */
  private drawShots(frame: OpenWorldSceneFrame): void {
    /*
     * **A cor de cada atirador, pela autoridade.** A aparência vem da entidade
     * que a sala publicou, e o catálogo diz de que cor são os tiros daquele
     * canhão. Um atirador fora de vista não tem aparência aqui: o tiro dele sai
     * na cor neutra e troca para a do canhão quando o casco aparecer, como no
     * duelo.
     */
    this.shotColourByOwner.clear();
    for (const entity of frame.entities) {
      this.shotColourByOwner.set(entity.id, pvpProjectileColour(entity.appearance));
    }
    this.shotsAlive.clear();
    /*
     * **O que a câmara mostra, com folga** (gate V28I.CG, cliente).
     *
     * A vista do quadro anterior: a câmara segue o casco depois deste passo, e a
     * folga de um décimo da vista cobre o que ela anda ou aproxima num quadro.
     */
    const view = this.cameras.main.worldView;
    const slack = 0.1 * Math.max(view.width, view.height);
    this.shotVisuals.inView = 0;
    this.shotsCulledNow.clear();
    for (const shot of frame.shots) {
      if (shot.base.card === 'd') {
        /*
         * **Um obus do Bombardeio de Profundidade não é um tiro** (gate
         * V28I.CF.1A): não fere, não é de ninguém que dispare, e o néon de um
         * projétil diria o contrário. A arte dele é a do golpe do boss.
         */
        continue;
      }
      const id = shot.base.id;
      const bolt = openWorldShotDrawsAsBolt(shot.base);
      this.presentShot(shot, this.drawn);
      if (bolt) {
        /*
         * **Um traço fora da vista não segura um sprite** (gate V28I.CG).
         *
         * Com trinta cascos 28× a cena tinha vinte e sete mil traços na lista de
         * desenho e dois mil e seiscentos no ecrã: o Phaser percorre e submete
         * todos os que estão visíveis, estejam onde estiverem. O corpo continua
         * no mapa da ligação e a posição dele continua a ser calculada aqui em
         * cada quadro; só o objeto volta ao pool, e regressa no quadro em que o
         * traço entra na vista. Tudo o que está DENTRO da vista é desenhado,
         * cada corpo com o seu traço — a folga é o traço inteiro à escala dele,
         * mais o décimo da vista.
         */
        const reach = this.shotReach(shot.base.scaleBps) + slack;
        if (
          this.drawn.x < view.x - reach ||
          this.drawn.x > view.x + view.width + reach ||
          this.drawn.y < view.y - reach ||
          this.drawn.y > view.y + view.height + reach
        ) {
          this.shotVisuals.culled += 1;
          this.shotsCulledNow.add(id);
          continue;
        }
        this.shotVisuals.inView += 1;
      }
      this.shotsAlive.add(id);
      const colour = bolt
        ? (this.shotColourByOwner.get(shot.base.owner) ?? pvpProjectileColour(null))
        : 0;
      let sprite = this.shots.get(id);
      if (sprite === undefined) {
        sprite = bolt ? this.acquireBolt(shot, colour) : this.acquireShot();
        this.shots.set(id, sprite);
        if (bolt) {
          this.shotColour.set(id, colour);
        }
      } else if (bolt && this.shotColour.get(id) !== colour) {
        /*
         * Um atirador cuja aparência chegou DEPOIS do tiro: o traço que já está
         * no ar passa a ser do canhão dele. `setTexture` reutiliza a textura em
         * cache; não gera nenhuma.
         */
        sprite.setTexture(ensureDashTexture(this, colour));
        this.shotColour.set(id, colour);
      }
      /*
       * **A posição VISUAL, no tique do cursor** (gates V28I.AK e V28I.BX.1).
       *
       * Um corpo linear pela aritmética da sala, os outros entre as suas duas
       * amostras. O sprite pousava na posição do último tique recebido e ficava
       * lá até à fotografia seguinte: medido, parado em 77% dos quadros com o
       * rato a mexer e em 97% com ele parado, a saltar 62 a 620 px de cada vez.
       *
       * Nada disto decide acerto, dano ou saída: a sala retira o corpo, e o
       * sprite sai no mesmo quadro.
       */
      sprite.setPosition(this.drawn.x, this.drawn.y);
      if (bolt) {
        this.emitWake(id, this.drawn.x, this.drawn.y, sprite.rotation, colour, shot.base.scaleBps);
      }
      this.shotVisuals.moved += 1;
    }
    /*
     * Percorrer e recolher, e só depois apagar: mexer no mapa a meio da
     * iteração era o que a cópia `[...entries()]` estava lá a evitar, e ela
     * custava um array do tamanho da população por quadro.
     */
    this.shotsGone.length = 0;
    for (const id of this.shots.keys()) {
      if (!this.shotsAlive.has(id)) {
        this.shotsGone.push(id);
      }
    }
    for (const id of this.shotsGone) {
      const sprite = this.shots.get(id);
      if (sprite !== undefined) {
        this.releaseShot(sprite);
      }
      this.shots.delete(id);
      this.shotWake.delete(id);
      this.shotColour.delete(id);
      /*
       * Um traço que só saiu da VISTA continua vivo: o rasto de amostras dele fica,
       * e é `pruneOpenWorldTracks` que o larga quando a sala o retirar.
       */
      if (!this.shotsCulledNow.has(id)) {
        this.releaseTrack(this.shotTracks, id);
      }
    }
    this.shotVisuals.inUse = this.shots.size;
    if (this.shots.size > this.shotVisuals.peakInUse) {
      this.shotVisuals.peakInUse = this.shots.size;
    }
  }

  /**
   * **Um objeto para este corpo: reciclado, ou novo se não houver.**
   *
   * A configuração é aplicada por inteiro **a cada aquisição**, e não só na
   * construção: textura, origem, profundidade, escala, rotação, alpha, tint,
   * modo de mistura, visibilidade e atividade. Um objeto que foi um traço e
   * volta como outro corpo não pode trazer nada da identidade anterior — e é
   * por isso que a origem também é reposta aqui, e não só no traço.
   */
  private acquireShot(): Phaser.GameObjects.Image {
    const sprite = this.takeShotObject();
    sprite.setTexture(PLACEHOLDER);
    sprite.setOrigin(0.5, 0.5);
    sprite.setDepth(8);
    sprite.setScale(0.09);
    sprite.setRotation(0);
    sprite.setAlpha(1);
    sprite.setTint(0x8ef6ff);
    sprite.setBlendMode(Phaser.BlendModes.ADD);
    sprite.setVisible(true);
    sprite.setActive(true);
    return sprite;
  }

  /**
   * **O traço de um tiro primário**, configurado por inteiro (gate V28I.CF.3).
   *
   * A textura já traz a cor, portanto a tinta é branca. A origem é a do duelo:
   * o ponto da simulação fica logo atrás da ponta. A escala é a da bitola com o
   * aumento de cadência e a escala física congelada, e o rumo é o da velocidade
   * da sala — um tiro primário voa a direito, e o rumo não muda no ar.
   */
  private acquireBolt(shot: OpenWorldSceneShot, colour: number): Phaser.GameObjects.Image {
    const sprite = this.takeShotObject();
    const scale = openWorldShotSpriteScale(this.shotGauge, shot.base.scaleBps);
    sprite.setTexture(ensureDashTexture(this, colour));
    sprite.setOrigin(DASH_ORIGIN_X, 0.5);
    sprite.setDepth(8);
    sprite.setScale(scale.x, scale.y);
    sprite.setRotation(openWorldShotHeading(shot.base.vx ?? 0, shot.base.vy ?? 0) ?? 0);
    sprite.setAlpha(1);
    sprite.clearTint();
    sprite.setBlendMode(Phaser.BlendModes.ADD);
    sprite.setVisible(true);
    sprite.setActive(true);
    return sprite;
  }

  /**
   * **Até onde um traço desenhado chega a partir do seu ponto**, em unidades do
   * mundo: a diagonal da textura à escala do sprite, que contém o traço em
   * qualquer rumo e com qualquer origem dentro dela. Uma entrada por escala.
   */
  private shotReach(scaleBps: number): number {
    const found = this.shotReachByScale.get(scaleBps);
    if (found !== undefined) {
      return found;
    }
    const scale = openWorldShotSpriteScale(this.shotGauge, scaleBps);
    const reach = Math.hypot(DASH_TEXTURE_WIDTH * scale.x, DASH_TEXTURE_HEIGHT * scale.y);
    this.shotReachByScale.set(scaleBps, reach);
    return reach;
  }

  /** Um objeto do pool, ou um novo se o pool estiver vazio. Sem teto visual. */
  private takeShotObject(): Phaser.GameObjects.Image {
    const recycled = this.shotPool.pop();
    if (recycled === undefined) {
      this.shotVisuals.created += 1;
      return this.add.image(0, 0, PLACEHOLDER);
    }
    this.shotVisuals.reused += 1;
    this.shotVisuals.pooled = this.shotPool.length;
    return recycled;
  }

  /**
   * **Uma lasca de rasto atrás de um traço**, do emissor da escala dele.
   *
   * Pelo rumo ANTERIOR, porque o rasto marca por onde o tiro passou; na cor do
   * canhão, para o rasto nunca dizer uma arma diferente do tiro que o deixa; e
   * a uma cadência, para noventa tiros deixarem rasto e não nevoeiro. O primeiro
   * quadro de um tiro não emite: ainda não há caminho atrás dele.
   */
  private emitWake(
    id: number,
    x: number,
    y: number,
    heading: number,
    colour: number,
    scaleBps: number,
  ): void {
    const wake = this.shotWake.get(id);
    if (wake === undefined) {
      this.shotWake.set(id, { heading, countdown: PROJECTILE_TRAIL_EVERY_FRAMES });
      return;
    }
    wake.countdown -= 1;
    if (wake.countdown <= 0) {
      wake.countdown = PROJECTILE_TRAIL_EVERY_FRAMES;
      const emitter = this.trailEmitterFor(scaleBps);
      emitter.particleTint = colour;
      emitter.particleRotate = Phaser.Math.RadToDeg(wake.heading);
      emitter.emitParticleAt(x, y, 1);
    }
    wake.heading = heading;
  }

  /**
   * **O emissor de rasto desta escala**, criado na primeira vez que é pedido.
   *
   * O do duelo, com a mesma textura, a mesma vida, a mesma opacidade e o mesmo
   * tecto — e com a ESPESSURA multiplicada pela escala do corpo. O comprimento
   * não cresce: foi o comprimento escalado que fez os rastos do duelo
   * sobreporem-se em linhas contínuas.
   */
  private trailEmitterFor(scaleBps: number): Phaser.GameObjects.Particles.ParticleEmitter {
    const found = this.trailEmitters.get(scaleBps);
    if (found !== undefined) {
      return found;
    }
    const streak = pvpStreakScale(this.shotGauge);
    const emitter = this.add.particles(0, 0, ensureStreakTexture(this), {
      speed: { min: 0, max: 12 * streak },
      lifespan: { ...PROJECTILE_TRAIL_LIFESPAN_MS },
      scaleX: { start: streak, end: streak * 0.25 },
      scaleY: {
        start: openWorldTrailThickness(streak * 0.9, scaleBps),
        end: openWorldTrailThickness(streak * 0.35, scaleBps),
      },
      alpha: { ...PROJECTILE_TRAIL_ALPHA },
      blendMode: Phaser.BlendModes.ADD,
      maxAliveParticles: PROJECTILE_TRAIL_MAX,
      emitting: false,
    });
    emitter.setDepth(7);
    this.trailEmitters.set(scaleBps, emitter);
    return emitter;
  }

  /**
   * **A saída autoritativa devolve o objeto, e não o destrói.**
   *
   * Ele deixa de representar a identidade antiga no mesmo instante: invisível,
   * inativo e fora da posição. **Sem desvanecimento**: um corpo que a sala
   * retirou já não existe, e um impacto confirmado é outro acontecimento, com a
   * duração aprovada dele, desenhado por outro caminho.
   */
  private releaseShot(sprite: Phaser.GameObjects.Image): void {
    sprite.setVisible(false);
    sprite.setActive(false);
    sprite.setPosition(0, 0);
    this.shotPool.push(sprite);
    this.shotVisuals.pooled = this.shotPool.length;
  }

  /** A fotografia dos contadores. Ler não muda nada e não decide nada. */
  shotVisualCounts(): Readonly<typeof this.shotVisuals> {
    return this.shotVisuals;
  }

  /**
   * **A proteção de fronteira, desenhada enquanto o servidor disser que existe.**
   *
   * Segue a posição DESENHADA do próprio casco e não a autoritativa, porque é
   * ali que o jogador vê a nave — um brilho na posição recebida ficaria meio
   * casco atrás durante todo o movimento.
   *
   * Uma instância, criada na primeira vez que é precisa e **escondida** em vez
   * de destruída a seguir: a proteção dura vinte segundos e volta ao fim de
   * três minutos, e reconstruir dez objetos por ativação seria trabalho por
   * ativação. É destruída de vez em `release`, com tudo o resto.
   */
  private syncProtection(frame: OpenWorldSceneFrame): void {
    const view = frame.protectedNow ? this.hulls.get(frame.me) : undefined;
    const entity = view === undefined ? undefined : frame.entities.find((e) => e.id === frame.me);
    if (view === undefined || entity === undefined) {
      this.protection?.update(null, this.time.now);
      return;
    }
    this.protection ??= new BastionFx(this, Phaser.BlendModes.ADD, HULL_DEPTH + 2);
    /*
     * O raio AUTORITATIVO do casco, que é o mesmo que decide colisão — e não a
     * largura do sprite, que depende da arte que o jogador tem equipada.
     */
    this.protection.update(
      { x: view.drawX, y: view.drawY, hullRadius: pvpWorldUnits(entity.radiusMu) },
      this.time.now,
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Câmara                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * **A câmara segue a posição DESENHADA, e nunca a recebida.**
   *
   * Seguir a autoritativa faria a câmara saltar três vezes por segundo enquanto
   * a nave desliza — o pior dos dois mundos. Seguindo a desenhada, ela é tão
   * suave quanto a nave é, por construção.
   *
   * Nas bordas ela **para**: os limites são os do quadrante, portanto não se vê
   * espaço que não existe. É o mesmo motivo pelo qual uma travessia parece uma
   * continuação — chega-se ao outro lado com o eixo perpendicular preservado, e
   * a câmara já está encostada ao limite correspondente.
   */
  private followCamera(frame: OpenWorldSceneFrame, delta: number): void {
    const me = this.hulls.get(frame.me);
    if (me === undefined) {
      return;
    }
    const camera = this.cameras.main;
    /*
     * **O zoom é DECLARADO, e não o que o Phaser calhar ter** (gate V28I.AN.1).
     *
     * A cena nunca chamava `setZoom`, portanto o zoom era o valor implícito 1 —
     * e um valor implícito não é uma decisão. A área de interesse do servidor
     * agora DERIVA daqui: se esta câmara afastasse, o servidor deixaria de
     * enviar coisas que passariam a caber no ecrã, e ninguém daria por isso até
     * ver naves a aparecer do nada.
     *
     * `openWorldZoomFor` é a vista canónica: nunca menos de 1, e o suficiente
     * para que a porção de mundo mostrada nunca passe de 3840 x 2160 pixeis
     * lógicos. Numa janela maior, a tela continua a encher a janela e a imagem
     * cresce — não há tarja, não há corte e não há efeito nenhum reduzido.
     *
     * É recalculado por quadro de propósito: `Phaser.Scale.RESIZE` muda a
     * largura quando a janela muda, e ler o valor é mais barato do que subscrever
     * o evento e arriscar um quadro com o zoom errado.
     */
    /*
     * **O zoom do jogador multiplica este, e nunca o substitui** (gates
     * V28I.BX.1, V28I.BX.2 e V28I.BX.2.1). Entre 0,3125 e 2 sobre a vista
     * canónica, com um chão: a área visível máxima, 6144 x 3456
     * (`openWorldEnvelopeZoomFor`). Numa janela 1920 x 1080 o chão é
     * exatamente 0,3125 — 60 % mais distância do que o 0,5 do BX.2 — e é a área
     * cuja diagonal inteira a área de interesse do servidor cobre: afastar nunca
     * mostra nada que o servidor não mande, e aproximar mostra menos.
     */
    this.playerZoomShown = easeOpenWorldPlayerZoom(
      this.playerZoomShown,
      this.playerZoomTarget,
      delta,
    );
    const zoom = Math.max(
      openWorldEnvelopeZoomFor(this.scale.width, this.scale.height),
      openWorldZoomFor(this.scale.width, this.scale.height) * this.playerZoomShown,
    );
    if (camera.zoom !== zoom) {
      camera.setZoom(zoom);
    }
    const side = frame.sideMu / MU;
    /*
     * **A meia-extensão VISÍVEL, e não a meia-tela.** Com zoom `z`, uma tela de
     * `w` pixeis mostra `w / z` unidades de mundo. Dividir só por dois prendia a
     * câmara longe de mais da borda do quadrante e escondia a própria parede.
     */
    const halfWidth = this.scale.width / (2 * zoom);
    const halfHeight = this.scale.height / (2 * zoom);
    const wantX = Math.max(halfWidth, Math.min(side - halfWidth, me.drawX));
    const wantY = Math.max(halfHeight, Math.min(side - halfHeight, me.drawY));
    /*
     * **A câmara é o casco desenhado, presa às bordas** (gate V28I.BX.1).
     *
     * Ela seguia o casco por uma fração do que faltava, e o casco seguia a sala
     * da mesma maneira: dois atrasos em série, e a câmara oscilava em relação à
     * nave — medido, o passo da câmara variava 85% por quadro com o rato
     * parado. O casco desenhado é agora contínuo por construção, e uma segunda
     * suavização só lhe juntava um atraso próprio. Uma travessia, uma
     * reconexão ou uma correção recomeçam o casco na posição nova, e a câmara
     * vai com ele no mesmo quadro, sem deslize e sem resto.
     */
    this.cameraX = wantX;
    this.cameraY = wantY;
    camera.centerOn(this.cameraX, this.cameraY);
  }

  /* ------------------------------------------------------------------ */
  /*  Apresentação contínua (gate V28I.BX.1)                             */
  /* ------------------------------------------------------------------ */

  /**
   * **Cada fotografia alimenta a apresentação, e só isso.**
   *
   * Regista o tique e o instante de chegada no cursor, e a posição de cada
   * casco e de cada corpo não linear na trilha dele. Não desenha nada: quem
   * desenha é o passo, no tique que o cursor disser.
   *
   * Uma descontinuidade — o tique a andar para trás ou a saltar mais de dois
   * segundos, ou a identidade própria a mudar numa travessia ou reconexão —
   * esquece tudo e recomeça na fotografia nova. Nenhuma trilha atravessa uma
   * descontinuidade.
   */
  private observeFrame(frame: OpenWorldSceneFrame): void {
    const now = performance.now();
    /*
     * **A chegada ao socket**, carimbada pela ligação (gate V28I.BX.2): o
     * intervalo entre fotografias é o da rede, sem uma renderização de React
     * pelo meio a tremê-lo.
     */
    const arrived = frame.receivedAt ?? now;
    const seen = this.presentation.observe(frame.tick, arrived);
    if (seen === 'reset' || frame.me !== this.presentedMe) {
      this.forgetPresentation();
      this.presentation.observe(frame.tick, arrived);
      this.presentedMe = frame.me;
    }
    this.seenHulls.clear();
    const side = frame.sideMu / MU;
    for (const entity of frame.entities) {
      this.seenHulls.add(entity.id);
      this.trackOf(this.tracks, entity.id).push(frame.tick, entity.x / MU, entity.y / MU);
      if (entity.id === frame.me) {
        const radius = entity.radiusMu / MU;
        this.ownHull.observe(
          {
            tick: frame.tick,
            x: entity.x / MU,
            y: entity.y / MU,
            moveX: entity.moveX ?? 0,
            moveY: entity.moveY ?? 0,
            syncAt: frame.ownSyncAt ?? null,
            minX: radius,
            maxX: side - radius,
            minY: radius,
            maxY: side - radius,
            immobile: entity.cards.frozen || frame.fishing !== null,
          },
          now,
        );
      }
    }
    this.seenShots.clear();
    this.seenLinear.clear();
    for (const shot of frame.shots) {
      if (shot.base.linear !== true) {
        this.seenShots.add(shot.base.id);
        this.trackOf(this.shotTracks, shot.base.id).push(frame.tick, shot.x / MU, shot.y / MU);
        continue;
      }
      this.seenLinear.add(shot.base.id);
      if (!this.shotOwnership.has(shot.base.id)) {
        this.shotOwnership.set(shot.base.id, this.bornFromMe(shot.base, frame));
      }
    }
    /*
     * Com a aba escondida o passo não corre, e um corpo que nasce e sai sem
     * nunca ter sprite nunca passaria pela libertação do desenho. Ver
     * `pruneOpenWorldTracks`.
     */
    pruneOpenWorldTracks(this.tracks, this.seenHulls, this.releaseHullTrack);
    pruneOpenWorldTracks(this.shotTracks, this.seenShots, this.releaseShotTrack);
    for (const id of this.shotOwnership.keys()) {
      if (!this.seenLinear.has(id)) {
        this.shotOwnership.delete(id);
      }
    }
  }

  /**
   * **Este corpo linear saiu do próprio casco?** Decidido uma vez, quando ele
   * aparece: o casco mais perto do ponto onde nasceu, no tique em que nasceu,
   * pelas trilhas autoritativas.
   *
   * Um corpo que aparece já velho — entrou na vista a meio do voo — não é do
   * próprio casco: os dele nascem sempre dentro da vista, e são vistos ao
   * nascer.
   */
  private bornFromMe(base: OpenWorldSceneShot['base'], frame: OpenWorldSceneFrame): boolean {
    if (
      base.baseX === undefined ||
      base.baseY === undefined ||
      base.baseTick === undefined ||
      frame.tick - base.baseTick > 2
    ) {
      return false;
    }
    const x = base.baseX / MU;
    const y = base.baseY / MU;
    let nearest = Number.POSITIVE_INFINITY;
    let owner = -1;
    for (const [id, track] of this.tracks) {
      if (!track.sampleAt(base.baseTick, this.probe)) {
        continue;
      }
      const distance = Math.hypot(this.probe.x - x, this.probe.y - y);
      if (distance < nearest) {
        nearest = distance;
        owner = id;
      }
    }
    return owner === frame.me;
  }

  /**
   * **Onde desenhar um corpo, no tique do cursor.** Escreve em `out`, em
   * unidades de desenho.
   *
   * Linear: `base + v x (tique - tique_da_base)`, a aritmética da sala. Os
   * outros: entre as duas amostras que rodeiam o cursor. Sem cursor, ou sem
   * nada disto, a posição recebida — exatamente o que acontecia antes.
   */
  private presentShot(shot: OpenWorldSceneShot, out: OpenWorldPoint): void {
    /*
     * **Os corpos lineares do PRÓPRIO casco no presente; os dos outros no
     * cursor** (gate V28I.BX.2).
     *
     * No cursor, um tiro acabado de nascer esperava à boca do canhão até o
     * cursor chegar ao tique dele — um tique e meio, com o casco já desenhado
     * no presente e à frente — e os que esperavam somavam-se em ADD num monte
     * claro e parado ao lado dos que já voavam. No presente, nasce a voar. Os
     * dos outros ficam no cursor, que é onde o casco que os disparou está
     * desenhado.
     */
    const tick = this.shotOwnership.get(shot.base.id) === true ? this.headTick : this.presentTick;
    if (tick !== null && openWorldLinearShotAt(shot.base, tick, out)) {
      out.x /= MU;
      out.y /= MU;
      return;
    }
    const cursor = this.presentTick;
    if (cursor !== null && this.shotTracks.get(shot.base.id)?.sampleAt(cursor, out)) {
      return;
    }
    out.x = pvpWorldUnits(shot.x);
    out.y = pvpWorldUnits(shot.y);
  }

  private trackOf(tracks: Map<number, OpenWorldTrack>, id: number): OpenWorldTrack {
    let track = tracks.get(id);
    if (track === undefined) {
      track = this.trackPool.pop() ?? new OpenWorldTrack();
      tracks.set(id, track);
    }
    return track;
  }

  private releaseTrack(tracks: Map<number, OpenWorldTrack>, id: number): void {
    const track = tracks.get(id);
    if (track !== undefined) {
      track.clear();
      this.trackPool.push(track);
      tracks.delete(id);
    }
  }

  /** Esquece o cursor e todas as trilhas. As trilhas voltam ao conjunto. */
  private forgetPresentation(): void {
    this.presentation.reset();
    this.presentTick = null;
    this.headTick = null;
    /* O próprio casco recomeça na fotografia nova, sem deslizar por um buraco. */
    this.ownHull.reset();
    this.shotOwnership.clear();
    for (const track of this.tracks.values()) {
      track.clear();
      this.trackPool.push(track);
    }
    this.tracks.clear();
    for (const track of this.shotTracks.values()) {
      track.clear();
      this.trackPool.push(track);
    }
    this.shotTracks.clear();
  }

  /**
   * **Larga o que estava a ser segurado, e diz-o à sala já** (gate V28I.BX.1).
   *
   * Pelo comando normal: a intenção neutra é uma transição, e a ligação envia
   * as transições na hora. Com a aba escondida o Phaser deixa de chamar o
   * passo, e esperar pelo próximo quadro seria esperar pelo regresso do jogador.
   */
  private releaseHeldIntent(): void {
    this.intent.clear();
    if (this.active) {
      this.publishIntent();
    }
  }

  /**
   * **A intenção deste instante, enviada e desenhada** (gate V28I.BX.2).
   *
   * Chamada a cada quadro e também no próprio gesto: premir, largar e o botão
   * do rato saem na hora, sem esperar pelo quadro seguinte — a ligação envia as
   * transições logo, e só a mira espera. O MESMO valor entra no diário do casco
   * previsto, portanto o que se vê é exatamente o que se pediu.
   */
  private publishIntent(now = performance.now()): void {
    const intent = this.intent.current(now);
    this.ownHull.steer(now, intent.mx, intent.my);
    this.ownAimX = intent.aimX;
    this.ownAimY = intent.aimY;
    this.options.bridge.sendToUi('open-world:command', intent);
  }

  /**
   * **A mira refeita a partir do ponteiro, a cada quadro.** O casco anda com o
   * rato parado, e o vetor casco → ponteiro muda com ele: sem isto a mira só
   * mudava quando o rato mexia. Parte da posição DESENHADA, que é onde o
   * jogador vê a nave.
   */
  private refreshAim(): void {
    if (!this.active || !this.controlsEnabled || this.pointerX === null) {
      return;
    }
    const me = this.hulls.get(this.frame.me);
    if (me === undefined) {
      return;
    }
    const world = this.cameras.main.getWorldPoint(this.pointerX, this.pointerY, this.aimPoint);
    this.intent.aimAt(me.drawX * MU, me.drawY * MU, world.x * MU, world.y * MU);
  }

  /** Recentra a câmara sem transição. Só depois de uma travessia. */
  recentre(): void {
    /*
     * **Esquecer, e não zerar.** A câmara segue o casco desenhado, e o casco do
     * outro lado nasce na posição que a primeira fotografia de lá disser —
     * nada é arrastado a partir de um canto, e nenhuma trilha do quadrante
     * anterior sobrevive (gate V28I.BX.1).
     */
    this.forgetPresentation();
    for (const view of this.hulls.values()) {
      view.container.destroy(true);
    }
    this.hulls.clear();
    /*
     * **A associação com a identidade antiga morre aqui** (gate V28I.AJ), e os
     * objetos ficam. Uma travessia, uma reconexão ou uma correção trocam o
     * mundo, e nenhum corpo do mundo anterior pode continuar a ser desenhado —
     * mas destruir dezenas de milhares de `Image` para os reconstruir do outro
     * lado seria pagar a travessia duas vezes.
     *
     * `releaseShot` deixa cada um invisível, inativo e sem posição, portanto
     * nenhuma apresentação atravessa a descontinuidade. O mapa fica vazio: a
     * identidade antiga deixou de ter representação no mesmo instante.
     */
    for (const sprite of this.shots.values()) {
      this.releaseShot(sprite);
    }
    this.shots.clear();
    this.shotVisuals.inUse = 0;
  }

  /* ------------------------------------------------------------------ */
  /*  Arte                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Pede uma textura que o build não pré-carregou, uma vez.
   *
   * `preload` corre antes de o servidor dizer quem está no quadrante, portanto
   * nenhum casco é conhecido nessa altura. Uma textura que não chegue deixa o
   * recurso no lugar em vez de um frame em branco.
   */
  /**
   * **Pede a folha da criatura daquele mapa, e recusa em voz alta** (V28I.BI).
   *
   * Um índice sem arte — fora de 1 a 20, ou reservado para depois do alfa —
   * lança, e a recusa é dita **uma vez por índice** pelo mesmo canal por onde um
   * casco em falta já é dito. Não há recuo para o índice 1: desenhar `alien1` no
   * lugar de `alien7` seria mentir sobre que criatura está no ecrã.
   */
  private requestMapBody(kind: OpenWorldMapBodyKind, mapIndex: number): void {
    if (this.refusedMapArt.has(`${kind}:${mapIndex}`)) {
      return;
    }
    try {
      requestOpenWorldMapBody({
        scene: this,
        manifest: this.options.manifest,
        kind,
        mapIndex,
        pending: this.pendingTextures,
      });
    } catch (error) {
      this.refusedMapArt.add(`${kind}:${mapIndex}`);
      this.options.bridge.sendToUi('open-world:scene-error', {
        stage: 'art',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private requestTexture(key: string, assetId: string): void {
    if (this.textures.exists(key) || this.pendingTextures.has(key)) {
      return;
    }
    try {
      const asset = findStaticAsset(this.options.manifest, assetId);
      this.pendingTextures.add(key);
      /*
       * **Pelo registo do jogo inteiro** (gate V28I.CF.3). A caça e o duelo
       * carregam as mesmas chaves de casco e de canhão, e um segundo carregador
       * para uma chave que outra cena já estava a buscar era o que punha
       * "Texture key already in use" na consola. `pendingTextures` passa a
       * dizer só "esta cena já está à espera", para um casco desenhado a cada
       * quadro não inscrever um aviso por quadro.
       */
      requestSharedTexture(this, key, assetUrl(this.options.manifest, asset), (loaded) => {
        this.pendingTextures.delete(key);
        if (loaded) {
          /* Os cascos foram construídos com o que havia; agora são reconstruídos. */
          for (const view of this.hulls.values()) {
            view.builtFrom = '';
          }
        }
      });
    } catch (error) {
      this.pendingTextures.delete(key);
      this.options.bridge.sendToUi('open-world:scene-error', {
        stage: 'art',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Larga tudo o que esta cena criou.
   *
   * Chamada pelo anfitrião quando a expedição acaba. Não é um `override`: o
   * ciclo de vida do Phaser chama `shutdown` por evento, e declarar um método
   * com esse nome sem o registar seria um método que ninguém chama.
   */
  /**
   * Larga tudo E cala a cena. Só para o fim do jogo, nunca para o fim de uma
   * expedição — quem se desinscrevesse ao fechar um quadrante nunca abriria o
   * seguinte.
   */
  private dispose(): void {
    this.release();
    for (const off of this.unsubscribe.splice(0)) {
      off();
    }
  }

  release(): void {
    /* A expedição seguinte começa na vista canónica (gate V28I.BX.1). */
    this.playerZoomTarget = 1;
    this.playerZoomShown = 1;
    this.starfield?.destroy();
    this.starfield = null;
    this.protection?.destroy();
    this.protection = null;
    this.cometArt?.destroy();
    this.cometArt = null;
    this.planetArt?.destroy();
    this.planetArt = null;
    this.mobArt?.destroy();
    this.mobArt = null;
    this.bossArt?.destroy();
    this.bossArt = null;
    /*
     * **Nada fica no ecrã.** Travessia, morte, saída da vista, fecho da sala e
     * `SHUTDOWN` passam todos por aqui, e um aviso órfão desenharia um perigo
     * que já não existe.
     */
    this.bossStrikeFx?.destroy();
    this.bossStrikeFx = null;
    this.waterArt?.destroy();
    this.waterArt = null;
    this.merchantArt?.destroy();
    this.merchantArt = null;
    this.boxArt?.destroy();
    this.boxArt = null;
    for (const ring of this.allyRings.values()) {
      ring.destroy();
    }
    this.allyRings.clear();
    this.cardBlasts?.destroy();
    this.cardBlasts = null;
    for (const fx of this.cardCircles.values()) {
      fx.destroy();
    }
    this.cardCircles.clear();
    for (const fx of this.cardLances.values()) {
      fx.destroy();
    }
    this.cardLances.clear();
    for (const container of this.summons.values()) {
      container.destroy(true);
    }
    this.summons.clear();
    this.summonBuilt.clear();
    this.boardOffer = false;
    /*
     * **Os sinais das cartas, todos.**
     *
     * Um por família e por entidade, largados aqui pela mesma porta que tudo o
     * resto: o fecho de um quadrante, uma travessia, uma reconstrução e o
     * `SHUTDOWN` chegam todos a este método, e nenhum deles pode deixar um selo
     * a flutuar sobre uma cena que já não existe.
     */
    for (const fx of this.cardStatus.values()) {
      fx.destroy();
    }
    this.cardStatus.clear();
    for (const fx of this.creatureStatus.values()) {
      fx.destroy();
    }
    this.creatureStatus.clear();
    for (const fx of this.cardMovement.values()) {
      fx.destroy();
    }
    this.cardMovement.clear();
    for (const fx of this.cardMalware.values()) {
      fx.destroy();
    }
    this.cardMalware.clear();
    for (const fx of this.cardMarks.values()) {
      fx.destroy();
    }
    this.cardMarks.clear();
    for (const fx of this.cardBastions.values()) {
      fx.destroy();
    }
    this.cardBastions.clear();
    for (const ring of this.cardAuras.values()) {
      ring.destroy();
    }
    this.cardAuras.clear();
    for (const tag of this.cloakTags.values()) {
      tag.destroy();
    }
    this.cloakTags.clear();
    for (const sprites of this.superCannons.values()) {
      for (const sprite of sprites) {
        sprite.destroy();
      }
    }
    this.superCannons.clear();
    this.chargingSpheres.length = 0;
    this.atomicBlasts?.destroy();
    this.atomicBlasts = null;
    /*
     * `recentre` devolve ao pool o que ainda estivesse em uso; a seguir o pool
     * inteiro é destruído. **Fechar a cena não deixa um objeto Phaser vivo**, e
     * reabrir começa com o pool vazio — que é o estado inicial previsto.
     */
    this.recentre();
    for (const sprite of this.shotPool.splice(0)) {
      sprite.destroy();
    }
    this.shotVisuals.pooled = 0;
    /*
     * Os emissores de rasto, um por escala, e o estado de cada rasto. Reabrir
     * começa sem nenhum, que é o estado inicial previsto (gate V28I.CF.3).
     */
    for (const emitter of this.trailEmitters.values()) {
      emitter.destroy();
    }
    this.trailEmitters.clear();
    this.shotWake.clear();
    this.shotColour.clear();
    this.shotColourByOwner.clear();
  }
}
